export default `precision highp float;
precision highp int;
out vec4 fragColor;

vec3 rotateX(vec3 p, float angle);
vec3 rotateY(vec3 p, float angle);
vec3 rotateZ(vec3 p, float angle);
vec3 rotate3D(vec3 p, vec3 angles);
vec3 rotate3DInv(vec3 p, vec3 angles);

float mapFractalOnly(vec3 p);

vec2 sdMandelbulbWithTrap(vec3 p, int iterations, float power);

const int MAX_STEPS = 128;
const float MIN_DIST = 0.001;
const float MAX_DIST = 100.0;
const float EPSILON = 0.0001;

const float PI = 3.14159265359;
const float TAU = 6.28318530718;
const float PHI = 1.61803398875;

/**
 * Rotation matrix around X axis
 */
mat3 matRotateX(float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat3(
    1.0, 0.0, 0.0,
    0.0, c, -s,
    0.0, s, c
  );
}

/**
 * Rotation matrix around Y axis
 */
mat3 matRotateY(float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat3(
    c, 0.0, s,
    0.0, 1.0, 0.0,
    -s, 0.0, c
  );
}

/**
 * Rotation matrix around Z axis
 */
mat3 matRotateZ(float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat3(
    c, -s, 0.0,
    s, c, 0.0,
    0.0, 0.0, 1.0
  );
}

/**
 * Generate camera ray direction from UV coordinates
 * Based on camera position and field of view
 */
vec3 getCameraRayDir(vec2 uv, vec3 camPos, vec3 lookAt, float fov) {
  vec3 forward = normalize(lookAt - camPos);
  vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), forward));
  vec3 up = cross(forward, right);

  float fovScale = tan(fov * 0.5 * PI / 180.0);
  vec3 rayDir = normalize(forward + fovScale * uv.x * right + fovScale * uv.y * up);

  return rayDir;
}

/**
 * Smooth minimum function (exponential)
 * Smoothly blends two distance fields
 */
float smin(float a, float b, float k) {
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}

/**
 * Smooth maximum function
 */
float smax(float a, float b, float k) {
  return -smin(-a, -b, k);
}

/**
 * Polynomial smooth minimum
 * Faster than exponential smin
 */
float sminPoly(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

/**
 * Map a value from one range to another
 */
float remap(float value, float inMin, float inMax, float outMin, float outMax) {
  return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
}

/**
 * Modulo operation that works for negative numbers
 */
float modulo(float x, float y) {
  return x - y * floor(x / y);
}

vec3 modulo(vec3 x, vec3 y) {
  return x - y * floor(x / y);
}

/**
 * Repeats space in all directions
 */
vec3 opRepeat(vec3 p, vec3 c) {
  return modulo(p + 0.5 * c, c) - 0.5 * c;
}

/**
 * Limits repetition to a finite domain
 */
vec3 opRepeatLimited(vec3 p, vec3 c, vec3 l) {
  return p - c * clamp(round(p / c), -l, l);
}

mat3 rotationAlign(vec3 from, vec3 to) {
  vec3 a = normalize(from);
  vec3 b = normalize(to);
  float c = dot(a, b);
  
  if (c > 0.9999) {
    return mat3(1.0);
  }
  
  if (c < -0.9999) {
    vec3 axis = normalize(abs(a.x) > 0.1 ? cross(a, vec3(0.0,1.0,0.0)) : cross(a, vec3(1.0,0.0,0.0)));
    float x = axis.x, y = axis.y, z = axis.z;
    
    return mat3(
      -1.0 + 2.0*x*x, 2.0*x*y,       2.0*x*z,
      2.0*y*x,       -1.0 + 2.0*y*y, 2.0*y*z,
      2.0*z*x,        2.0*z*y,      -1.0 + 2.0*z*z
    );
  }
  vec3 v = cross(a, b);
  float k = 1.0 / (1.0 + c);
  
  return mat3(
    v.x*v.x*k + c,     v.x*v.y*k - v.z,  v.x*v.z*k + v.y,
    v.y*v.x*k + v.z,   v.y*v.y*k + c,    v.y*v.z*k - v.x,
    v.z*v.x*k - v.y,   v.z*v.y*k + v.x,  v.z*v.z*k + c
  );
}
/**
 * Sphere - Exact distance
 * @param p Point in space
 * @param r Radius
 */
float sdSphere(vec3 p, float r) {
  return length(p) - r;
}

/**
 * Box - Exact distance
 * @param p Point in space
 * @param b Box dimensions (half extents)
 */
float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

/**
 * Plane - Exact distance
 * @param p Point in space
 * @param n Normal vector (should be normalized)
 * @param h Height offset
 */
float sdPlane(vec3 p, vec3 n, float h) {
  return dot(p, n) + h;
}

/**
 * Infinite plane (XZ plane at y=0)
 */
float sdPlaneXZ(vec3 p) {
  return p.y;
}

/**
 * Torus - Exact distance
 * @param p Point in space
 * @param t vec2(major radius, minor radius)
 */
float sdTorus(vec3 p, vec2 t) {
  vec2 q = vec2(length(p.xz) - t.x, p.y);
  return length(q) - t.y;
}

/**
 * Cylinder - Exact distance (infinite in Y)
 * @param p Point in space
 * @param r Radius
 */
float sdCylinder(vec3 p, float r) {
  return length(p.xz) - r;
}

/**
 * Capped Cylinder - Exact distance
 * @param p Point in space
 * @param h Height
 * @param r Radius
 */
float sdCappedCylinder(vec3 p, float h, float r) {
  vec2 d = abs(vec2(length(p.xz), p.y)) - vec2(r, h);
  return min(max(d.x, d.y), 0.0) + length(max(d, 0.0));
}

/**
 * Cone - Exact distance
 * @param p Point in space
 * @param c vec2(sin(angle), cos(angle))
 * @param h Height
 */
float sdCone(vec3 p, vec2 c, float h) {
  vec2 q = h * vec2(c.x / c.y, -1.0);
  vec2 w = vec2(length(p.xz), p.y);
  vec2 a = w - q * clamp(dot(w, q) / dot(q, q), 0.0, 1.0);
  vec2 b = w - q * vec2(clamp(w.x / q.x, 0.0, 1.0), 1.0);
  float k = sign(q.y);
  float d = min(dot(a, a), dot(b, b));
  float s = max(k * (w.x * q.y - w.y * q.x), k * (w.y - q.y));
  return sqrt(d) * sign(s);
}

/**
 * Octahedron - Exact distance
 * @param p Point in space
 * @param s Size
 */
float sdOctahedron(vec3 p, float s) {
  p = abs(p);
  float m = p.x + p.y + p.z - s;
  vec3 q;
  if (3.0 * p.x < m) q = p.xyz;
  else if (3.0 * p.y < m) q = p.yzx;
  else if (3.0 * p.z < m) q = p.zxy;
  else return m * 0.57735027;

  float k = clamp(0.5 * (q.z - q.y + s), 0.0, s);
  return length(vec3(q.x, q.y - s + k, q.z - k));
}

/**
 * Capsule - Exact distance
 * @param p Point in space
 * @param a First endpoint
 * @param b Second endpoint
 * @param r Radius
 */
float sdCapsule(vec3 p, vec3 a, vec3 b, float r) {
  vec3 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - r;
}

/**
 * Tetrahedron - Exact distance (IQ variant)
 * @param p Point in space
 * @param s Size (edge-related)
 */
float sdTetrahedron(vec3 p, float s) {
  const float k = 0.57735026919; 
  p = abs(p);
  float m = p.x + p.y + p.z - s;
  vec3 q;
  if (3.0 * p.x < m) q = p.xyz;
  else if (3.0 * p.y < m) q = p.yzx;
  else if (3.0 * p.z < m) q = p.zxy;
  else return m * k;
  float k2 = clamp(0.5 * (q.z - q.y + s), 0.0, s);
  return length(vec3(q.x, q.y - s + k2, q.z - k2));
}
/**
 * Union - Combines two shapes
 * Returns the minimum distance (closer surface)
 */
float opUnion(float d1, float d2) {
  return min(d1, d2);
}

/**
 * Subtraction - Subtracts d2 from d1
 * Removes the second shape from the first
 */
float opSubtraction(float d1, float d2) {
  return max(-d2, d1);
}

/**
 * Intersection - Intersection of two shapes
 * Returns only the overlapping volume
 */
float opIntersection(float d1, float d2) {
  return max(d1, d2);
}

/**
 * Smooth Union - Smoothly blends two shapes
 * @param k Smoothness factor (larger = smoother)
 */
float opSmoothUnion(float d1, float d2, float k) {
  float h = clamp(0.5 + 0.5 * (d2 - d1) / k, 0.0, 1.0);
  return mix(d2, d1, h) - k * h * (1.0 - h);
}

/**
 * Smooth Subtraction
 */
float opSmoothSubtraction(float d1, float d2, float k) {
  float h = clamp(0.5 - 0.5 * (d2 + d1) / k, 0.0, 1.0);
  return mix(d1, -d2, h) + k * h * (1.0 - h);
}

/**
 * Smooth Intersection
 */
float opSmoothIntersection(float d1, float d2, float k) {
  float h = clamp(0.5 - 0.5 * (d2 - d1) / k, 0.0, 1.0);
  return mix(d2, d1, h) + k * h * (1.0 - h);
}

/**
 * Elongation - Stretches a shape along an axis
 */
vec3 opElongate(vec3 p, vec3 h) {
  return p - clamp(p, -h, h);
}

/**
 * Rounding - Rounds the edges of a shape
 * @param d Distance field
 * @param r Rounding radius
 */
float opRound(float d, float r) {
  return d - r;
}

/**
 * Onion - Creates a shell/hollow version of a shape
 * @param d Distance field
 * @param thickness Shell thickness
 */
float opOnion(float d, float thickness) {
  return abs(d) - thickness;
}

/**
 * Extrusion - Extrudes a 2D shape into 3D
 */
float opExtrusion(vec3 p, float sdf2d, float h) {
  vec2 w = vec2(sdf2d, abs(p.z) - h);
  return min(max(w.x, w.y), 0.0) + length(max(w, 0.0));
}

/**
 * Revolution - Revolves a 2D shape around Y axis
 */
float opRevolution(vec3 p, float sdf2d, float offset) {
  vec2 q = vec2(length(p.xz) - offset, p.y);
  return sdf2d;
}

/**
 * Twist - Twists a shape around Y axis
 */
vec3 opTwist(vec3 p, float k) {
  float c = cos(k * p.y);
  float s = sin(k * p.y);
  mat2 m = mat2(c, -s, s, c);
  return vec3(m * p.xz, p.y);
}

/**
 * Bend - Bends a shape
 */
vec3 opBend(vec3 p, float k) {
  float c = cos(k * p.x);
  float s = sin(k * p.x);
  mat2 m = mat2(c, -s, s, c);
  return vec3(m * p.xy, p.z);
}

/**
 * Repetition - Repeats space in all directions
 */
vec3 opRep(vec3 p, vec3 c) {
  return mod(p + 0.5 * c, c) - 0.5 * c;
}

/**
 * Limited Repetition - Repeats within bounds
 */
vec3 opRepLim(vec3 p, vec3 c, vec3 l) {
  return p - c * clamp(round(p / c), -l, l);
}

/**
 * Scale - Scales a distance field
 * Note: Must divide the result by the scale factor
 */
vec3 opScale(vec3 p, float s) {
  return p / s;
}

/**
 * Symmetry - Mirrors across a plane
 */
vec3 opSymX(vec3 p) {
  p.x = abs(p.x);
  return p;
}

vec3 opSymY(vec3 p) {
  p.y = abs(p.y);
  return p;
}

vec3 opSymZ(vec3 p) {
  p.z = abs(p.z);
  return p;
}

vec3 opSymXYZ(vec3 p) {
  return abs(p);
}
float sdMenger(vec3 p, int iterations) {
    
    float d = sdBox(p, vec3(1.0));

    
    float s = 1.0;

    for(int i = 0; i < iterations; i++) {
        
        vec3 a = mod(p * s, 2.0) - 1.0;
        s *= 3.0;
        vec3 r = abs(1.0 - 3.0 * abs(a));

        
        float da = max(r.x, r.y);
        float db = max(r.y, r.z);
        float dc = max(r.z, r.x);
        float c = (min(da, min(db, dc)) - 1.0) / s;

        
        d = max(d, c);
    }

    return d;
}

float sdMengerUniform(vec3 p, int maxIterations) {
    return sdMenger(p, maxIterations);
}

float sdMengerAnimated(vec3 p, int iterations, float time) {
    
    p = rotateY(p, time * 0.2);
    p = rotateX(p, time * 0.15);

    return sdMenger(p, iterations);
}

float sdMengerScaled(vec3 p, int iterations, float scale) {
    
    p /= scale;

    
    return sdMenger(p, iterations) * scale;
}
float sdMandelbulb(vec3 p, int iterations, float power) {
    vec3 z = p;
    float dr = 1.0;
    float r = 0.0;

    for(int i = 0; i < iterations; i++) {
        r = length(z);

        
        if(r > 2.0) break;

        
        float theta = acos(z.z / r);
        float phi = atan(z.y, z.x);

        
        dr = pow(r, power - 1.0) * power * dr + 1.0;

        
        float zr = pow(r, power);
        theta = theta * power;
        phi = phi * power;

        
        z = zr * vec3(
            sin(theta) * cos(phi),
            sin(phi) * sin(theta),
            cos(theta)
        );

        
        z += p;
    }

    
    r = max(r, 1e-6); 
    return 0.5 * log(r) * r / dr;
}

float sdMandelbulbUniform(vec3 p, int maxIterations, float power) {
    return sdMandelbulb(p, maxIterations, power);
}

float sdMandelbulbAnimated(vec3 p, int iterations, float power, float time) {
    
    p = rotateY(p, time * 0.1);
    p = rotateX(p, sin(time * 0.05) * 0.3);

    return sdMandelbulb(p, iterations, power);
}

float sdMandelbulbScaled(vec3 p, int iterations, float power, float scale) {
    
    p /= scale;

    
    return sdMandelbulb(p, iterations, power) * scale;
}

float sdJulia(vec3 p, vec4 c, int iterations) {
    vec4 z = vec4(p, 0.0);
    float dr = 1.0;
    float r = 0.0;

    for(int i = 0; i < iterations; i++) {
        r = length(z);
        if(r > 2.0) break;

        
        dr = 2.0 * r * dr + 1.0;

        
        z = vec4(
            z.x*z.x - z.y*z.y - z.z*z.z - z.w*z.w,
            2.0*z.x*z.y,
            2.0*z.x*z.z,
            2.0*z.x*z.w
        ) + c;
    }

    r = max(r, 1e-6); 
    return 0.5 * log(r) * r / dr;
}
vec3 boxFold(vec3 z, float foldingLimit) {
    return clamp(z, -foldingLimit, foldingLimit) * 2.0 - z;
}

vec3 sphereFold(vec3 z, float minRadius, float fixedRadius, out float factor) {
    float r2 = dot(z, z);
    float minRadius2 = minRadius * minRadius;
    float fixedRadius2 = fixedRadius * fixedRadius;

    if (r2 < minRadius2) {
        
        factor = fixedRadius2 / minRadius2;
        return z * factor;
    } else if (r2 < fixedRadius2) {
        
        factor = fixedRadius2 / r2;
        return z * factor;
    } else {
        
        factor = 1.0;
        return z;
    }
}

float sdMandelbox(vec3 p, int iterations, float scale, float foldingLimit, float minRadius, float fixedRadius) {
    vec3 z = p;
    float dr = 1.0; 
    float r = length(z);

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        
        z = boxFold(z, foldingLimit);

        
        float factor;
        z = sphereFold(z, minRadius, fixedRadius, factor);
        dr = dr * abs(factor);

        
        z = z * scale + p;
        dr = dr * abs(scale) + 1.0;

        r = length(z);
        if (r > 10.0) break;
    }

    
    
    return r / abs(dr);
}

float sdMandelboxSimple(vec3 p, int iterations, float scale) {
    return sdMandelbox(p, iterations, scale, 1.0, 0.5, 1.0);
}

float sdMandelboxVariable(vec3 p, int iterations, float scale, float foldingLimit, float minRadius) {
    vec3 z = p;
    float dr = 1.0;
    float fixedRadius = 1.0;
    float minRadius2 = minRadius * minRadius;
    float fixedRadius2 = fixedRadius * fixedRadius;

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        
        z = boxFold(z, foldingLimit);

        
        float r2 = dot(z, z);
        if (r2 < minRadius2) {
            float factor = fixedRadius2 / minRadius2;
            z *= factor;
            dr *= factor;
        } else if (r2 < fixedRadius2) {
            float factor = fixedRadius2 / r2;
            z *= factor;
            dr *= factor;
        }

        
        z = z * scale + p;
        dr = dr * abs(scale) + 1.0;
    }

    return length(z) / abs(dr);
}

struct MandelboxResult {
    float dist;
    float orbitTrap;
    float sphereFolds; 
};

MandelboxResult sdMandelboxColored(vec3 p, int iterations, float scale) {
    vec3 z = p;
    float dr = 1.0;
    float minDist = 1000.0;
    float sphereFoldCount = 0.0;

    float foldingLimit = 1.0;
    float minRadius = 0.5;
    float fixedRadius = 1.0;
    float minRadius2 = minRadius * minRadius;
    float fixedRadius2 = fixedRadius * fixedRadius;

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        
        z = boxFold(z, foldingLimit);

        
        float r2 = dot(z, z);
        if (r2 < minRadius2) {
            float factor = fixedRadius2 / minRadius2;
            z *= factor;
            dr *= factor;
            sphereFoldCount += 1.0;
        } else if (r2 < fixedRadius2) {
            float factor = fixedRadius2 / r2;
            z *= factor;
            dr *= factor;
            sphereFoldCount += 0.5;
        }

        
        z = z * scale + p;
        dr = dr * abs(scale) + 1.0;

        
        minDist = min(minDist, length(z));
    }

    MandelboxResult result;
    result.dist = length(z) / abs(dr);
    result.orbitTrap = minDist;
    result.sphereFolds = sphereFoldCount;

    return result;
}

float sdMandelboxAnimated(vec3 p, int iterations, float scale, float time) {
    
    float foldingLimit = 1.0 + 0.2 * sin(time * 0.5);
    float minRadius = 0.5 + 0.1 * cos(time * 0.7);

    return sdMandelbox(p, iterations, scale, foldingLimit, minRadius, 1.0);
}
vec3 tetrahedralFold(vec3 z) {
    
    

    
    if (z.x + z.y < 0.0) z.xy = -z.yx;

    
    if (z.x + z.z < 0.0) z.xz = -z.zx;

    
    if (z.y + z.z < 0.0) z.zy = -z.yz;

    return z;
}

vec3 tetrahedralFoldAccurate(vec3 z) {
    
    vec3 n1 = normalize(vec3( 1.0,  1.0,  1.0));
    vec3 n2 = normalize(vec3(-1.0, -1.0,  1.0));
    vec3 n3 = normalize(vec3( 1.0, -1.0, -1.0));
    vec3 n4 = normalize(vec3(-1.0,  1.0, -1.0));

    
    z -= 2.0 * min(0.0, dot(z, n1)) * n1;
    z -= 2.0 * min(0.0, dot(z, n2)) * n2;
    z -= 2.0 * min(0.0, dot(z, n3)) * n3;
    z -= 2.0 * min(0.0, dot(z, n4)) * n4;

    return z;
}

float sdSierpinskiWithBase(vec3 p, int iterations, float scale, float baseSize) {
    vec3 z = p;
    float s = 1.0;
    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;
        z = tetrahedralFold(z);
        z = z * scale - vec3(scale - 1.0);
        s *= scale;
    }
    return sdTetrahedron(z, baseSize) / max(1.0, s);
}

float sdSierpinski(vec3 p, int iterations, float scale) {
    return sdSierpinskiWithBase(p, iterations, scale, 0.6);
}

float sdSierpinskiSimple(vec3 p, int iterations, float scale) {
    vec3 z = p;
    float dist = 0.0;

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        
        z = tetrahedralFold(z);

        
        z *= scale;
        z -= vec3(scale - 1.0);
    }

    
    
    return (length(z) - 2.0) / pow(scale, float(iterations));
}

float sdSierpinskiCustom(vec3 p, int iterations, float scale, vec3 offset) {
    vec3 z = p;
    float r = length(z);
    float dr = 1.0;

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        z = tetrahedralFold(z);
        z = z * scale - offset * (scale - 1.0);
        dr = dr * scale;
        r = length(z);
    }

    return (r - 2.0) / dr * 0.25;
}

struct SierpinskiResult {
    float dist;
    float orbitTrap;
};

SierpinskiResult sdSierpinskiColored(vec3 p, int iterations, float scale) {
    vec3 z = p;
    float r = length(z);
    float dr = 1.0;
    float minDist = 1000.0; 

    for (int i = 0; i < 20; i++) {
        if (i >= iterations) break;

        z = tetrahedralFold(z);
        z = z * scale - vec3(scale - 1.0);
        dr = dr * scale;
        r = length(z);

        
        minDist = min(minDist, r);
    }

    SierpinskiResult result;
    result.dist = (r - 2.0) / dr * 0.25;
    result.orbitTrap = minDist;

    return result;
}
vec3 palette(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
    
    
    return a + b * cos(6.28318 * (c * t + d));
}

vec3 gradientMap(float t, vec3 color1, vec3 color2, vec3 color3) {
    if (t < 0.5) {
        return mix(color1, color2, t * 2.0);
    } else {
        return mix(color2, color3, (t - 0.5) * 2.0);
    }
}

vec3 gradientMap5(float t, vec3 c1, vec3 c2, vec3 c3, vec3 c4, vec3 c5) {
    t = clamp(t, 0.0, 1.0);
    if (t < 0.25) {
        return mix(c1, c2, t * 4.0);
    } else if (t < 0.5) {
        return mix(c2, c3, (t - 0.25) * 4.0);
    } else if (t < 0.75) {
        return mix(c3, c4, (t - 0.5) * 4.0);
    } else {
        return mix(c4, c5, (t - 0.75) * 4.0);
    }
}

vec3 paletteDeepOcean(float t) {
    
    t = clamp(t, 0.0, 1.0);
    
    t = smoothstep(0.05, 0.95, t);
    vec3 c1 = vec3(0.03, 0.06, 0.12); 
    vec3 c2 = vec3(0.06, 0.16, 0.35); 
    vec3 c3 = vec3(0.08, 0.35, 0.58); 
    vec3 c4 = vec3(0.10, 0.65, 0.65); 
    vec3 c5 = vec3(0.85, 0.95, 0.98); 
    vec3 col = gradientMap5(t, c1, c2, c3, c4, c5);
    
    col = pow(col, vec3(0.9));
    return col;
}

vec3 paletteMoltenLava(float t) {
    return palette(t,
        vec3(0.5, 0.3, 0.2),  
        vec3(0.5, 0.3, 0.2),  
        vec3(1.0, 1.0, 0.5),  
        vec3(0.8, 0.2, 0.0)   
    );
}

vec3 paletteElectric(float t) {
    return palette(t,
        vec3(0.5, 0.5, 0.5),  
        vec3(0.5, 0.5, 0.5),  
        vec3(1.0, 1.0, 1.0),  
        vec3(0.0, 0.33, 0.67) 
    );
}

vec3 paletteOrganic(float t) {
    return palette(t,
        vec3(0.4, 0.5, 0.3),  
        vec3(0.3, 0.4, 0.2),  
        vec3(1.0, 1.0, 0.5),  
        vec3(0.2, 0.4, 0.0)   
    );
}

vec3 paletteMonochrome(float t) {
    return palette(t,
        vec3(0.3, 0.3, 0.3),  
        vec3(0.4, 0.4, 0.5),  
        vec3(1.0, 1.0, 1.0),  
        vec3(0.0, 0.0, 0.3)   
    );
}

vec3 paletteDeepAbyss(float t) {
    t = clamp(t, 0.0, 1.0);
    t = smoothstep(0.02, 0.98, t);
    vec3 c1 = vec3(0.010, 0.018, 0.030); 
    vec3 c2 = vec3(0.020, 0.060, 0.110); 
    vec3 c3 = vec3(0.030, 0.120, 0.180); 
    vec3 c4 = vec3(0.050, 0.220, 0.220); 
    vec3 c5 = vec3(0.300, 0.600, 0.750); 
    vec3 col = gradientMap5(t, c1, c2, c3, c4, c5);
    col = pow(col, vec3(0.95));
    return col;
}

vec3 paletteTropicalSea(float t) {
    t = clamp(t, 0.0, 1.0);
    t = smoothstep(0.0, 1.0, t);
    vec3 c1 = vec3(0.020, 0.150, 0.180); 
    vec3 c2 = vec3(0.050, 0.450, 0.550); 
    vec3 c3 = vec3(0.050, 0.750, 0.750); 
    vec3 c4 = vec3(0.450, 0.900, 0.750); 
    vec3 c5 = vec3(0.980, 0.980, 0.850); 
    vec3 col = gradientMap5(t, c1, c2, c3, c4, c5);
    
    col = mix(col, vec3(1.0, 0.97, 0.92), smoothstep(0.8, 1.0, t) * 0.25);
    return col;
}

vec3 colorOrbitTrapPoint(float minDist, int paletteId) {
    float t = clamp(minDist * 0.5, 0.0, 1.0);

    if (paletteId == 0) return paletteDeepOcean(t);
    if (paletteId == 1) return paletteMoltenLava(t);
    if (paletteId == 2) return paletteElectric(t);
    if (paletteId == 3) return paletteOrganic(t);
    if (paletteId == 5) return paletteDeepAbyss(t);
    if (paletteId == 6) return paletteTropicalSea(t);
    return paletteMonochrome(t);
}

struct PlaneTraps {
    float x; 
    float y; 
    float z; 
};

vec3 colorOrbitTrapPlanes(PlaneTraps traps, int paletteId) {
    
    float minTrap = min(min(traps.x, traps.y), traps.z);
    float t = clamp(minTrap, 0.0, 1.0);

    if (paletteId == 0) return paletteDeepOcean(t);
    if (paletteId == 1) return paletteMoltenLava(t);
    if (paletteId == 2) return paletteElectric(t);
    if (paletteId == 3) return paletteOrganic(t);
    if (paletteId == 5) return paletteDeepAbyss(t);
    if (paletteId == 6) return paletteTropicalSea(t);
    return paletteMonochrome(t);
}

vec3 colorByDistance(float dist, float maxDist, int paletteId) {
    float t = clamp(dist / maxDist, 0.0, 1.0);

    if (paletteId == 0) return paletteDeepOcean(t);
    if (paletteId == 1) return paletteMoltenLava(t);
    if (paletteId == 2) return paletteElectric(t);
    if (paletteId == 3) return paletteOrganic(t);
    if (paletteId == 5) return paletteDeepAbyss(t);
    if (paletteId == 6) return paletteTropicalSea(t);
    return paletteMonochrome(t);
}

vec3 colorBySteps(float steps, float maxSteps, int paletteId) {
    float t = clamp(steps / maxSteps, 0.0, 1.0);

    if (paletteId == 0) return paletteDeepOcean(t);
    if (paletteId == 1) return paletteMoltenLava(t);
    if (paletteId == 2) return paletteElectric(t);
    if (paletteId == 3) return paletteOrganic(t);
    if (paletteId == 5) return paletteDeepAbyss(t);
    if (paletteId == 6) return paletteTropicalSea(t);
    return paletteMonochrome(t);
}

vec3 colorByNormal(vec3 normal) {
    
    return normal * 0.5 + 0.5;
}

vec3 colorCombined(float orbitTrap, float dist, vec3 normal, int paletteId) {
    
    vec3 colorA = colorOrbitTrapPoint(orbitTrap, paletteId);
    vec3 colorB = colorByDistance(dist, 10.0, paletteId);

    
    float blend = abs(normal.y) * 0.5 + 0.5;
    return mix(colorA, colorB, blend);
}

vec3 hsv2rgb(vec3 c) {
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

vec3 rgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

    float d = q.x - min(q.w, q.y);
    float e = 1.0e-10;
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

const float DEC_PI  = 3.14159265359;
const float DEC_TAU = 6.28318530718;
const float DEC_PHI = 1.61803398875;

float dec_saturate(float x) { return clamp(x, 0.0, 1.0); }
float dec_length2(vec2 v) { return dot(v,v); }
float dec_length2(vec3 v) { return dot(v,v); }

#ifndef DEC_GDF_COUNT
  #define DEC_GDF_COUNT 19
  const vec3 DEC_GDF[DEC_GDF_COUNT] = vec3[](
    normalize(vec3(1.0, 0.0, 0.0)),
    normalize(vec3(0.0, 1.0, 0.0)),
    normalize(vec3(0.0, 0.0, 1.0)),
    normalize(vec3(1.0, 1.0, 1.0)),
    normalize(vec3(-1.0, 1.0, 1.0)),
    normalize(vec3(1.0, -1.0, 1.0)),
    normalize(vec3(1.0, 1.0, -1.0)),
    normalize(vec3(0.0, 1.0, DEC_PHI + 1.0)),
    normalize(vec3(0.0, -1.0, DEC_PHI + 1.0)),
    normalize(vec3(DEC_PHI + 1.0, 0.0, 1.0)),
    normalize(vec3(-(DEC_PHI + 1.0), 0.0, 1.0)),
    normalize(vec3(1.0, DEC_PHI + 1.0, 0.0)),
    normalize(vec3(-1.0, DEC_PHI + 1.0, 0.0)),
    normalize(vec3(0.0, DEC_PHI, 1.0)),
    normalize(vec3(0.0, -DEC_PHI, 1.0)),
    normalize(vec3(1.0, 0.0, DEC_PHI)),
    normalize(vec3(-1.0, 0.0, DEC_PHI)),
    normalize(vec3(DEC_PHI, 1.0, 0.0)),
    normalize(vec3(-DEC_PHI, 1.0, 0.0))
  );
#endif

#ifndef DEC_GDF_FNS
#define DEC_GDF_FNS 1
float fGDF(vec3 p, float r, float e, int begin, int end) {
  float d = 0.0;
  for (int i = 0; i < DEC_GDF_COUNT; ++i) {
    if (i >= begin && i <= end) {
      d += pow(abs(dot(p, DEC_GDF[i])), e);
    }
  }
  return pow(max(d, 1e-8), 1.0 / max(e, 1e-6)) - r;
}
float fGDF(vec3 p, float r, int begin, int end) {
  float d = 0.0;
  for (int i = 0; i < DEC_GDF_COUNT; ++i) {
    if (i >= begin && i <= end) {
      d = max(d, abs(dot(p, DEC_GDF[i])));
    }
  }
  return d - r;
}
#endif

#ifndef DEC_USER_DEFINED

float decUserDE(vec3 p) { return sdBox(p, vec3(1.0)); }
#endif

uniform float u_time;
uniform vec2 u_resolution;
uniform vec3 u_cameraPos;
uniform vec3 u_cameraTarget;
uniform float u_fov;

uniform vec3 u_camRight;
uniform vec3 u_camUp;

#ifdef FRAC_TYPE

const int u_fractalType = FRAC_TYPE; 
#else
uniform int u_fractalType; 
#endif
uniform int u_iterations;
uniform float u_fractalPower; 
uniform float u_fractalScale;
uniform vec3 u_rotation; 
uniform vec3 u_zoomCenter; 

uniform vec3 u_lightPos;
uniform vec3 u_lightColor;
uniform bool u_tintDiffuse; 
uniform float u_ambientStrength;
uniform float u_diffuseStrength;
uniform float u_specularStrength;
uniform float u_shininess;
uniform bool u_fogEnabled;
uniform int u_fogType; 
uniform float u_fogDensity;
uniform float u_fogNear;
uniform float u_fogFar;
uniform vec3 u_fogColor;
uniform vec3 u_backgroundColor;
uniform int u_maxSteps;
uniform float u_stepRelaxation;
uniform bool u_aoEnabled;
uniform bool u_softShadowsEnabled;
uniform int u_softShadowSteps;
uniform float u_shadowSharpness;

uniform float u_shadowBiasBase;   
uniform float u_shadowBiasSlope;  
uniform float u_shadowBiasAngle;  
uniform float u_shadowPlaneBias;  
uniform float u_shadowDitherStrength; 
uniform float u_normalEpsilon;

uniform bool u_adaptiveRelaxation; 
uniform float u_relaxationMin; 
uniform float u_relaxationMax; 
uniform bool u_enableDithering; 
uniform float u_ditheringStrength; 
uniform bool u_useBlueNoise; 
uniform float u_blueNoiseScale; 
uniform bool u_blueNoiseTemporalJitter; 
uniform bool u_ditherFog; 
uniform bool u_enableDistanceLOD; 
uniform bool u_enableBudgetLOD;   
uniform float u_lodNear; 
uniform float u_lodFar; 
uniform float u_budgetStepsFarFactor; 
uniform float u_farShadowSkipFactor;  
uniform int u_aoMinSamples; 
uniform int u_softShadowMinSteps; 

uniform bool u_enableBoundsCulling; 
uniform float u_boundsCullMargin;   

uniform bool u_floorEnabled;
uniform vec3 u_floorColorA;
uniform vec3 u_floorColorB;
uniform bool u_floorReceiveShadows; 
uniform int u_cullMode;             

uniform int u_texApplyTarget;

uniform int u_floorTexMode;

uniform bool  u_floorIgnoreWarp; 
uniform float u_floorBumpScale;  
uniform float u_floorSpecScale;  

uniform float u_floorTexDisableDist; 
uniform bool  u_floorTexAutoDisable; 
uniform float u_floorTexAutoMul;     
uniform float u_floorFadeNear;       
uniform float u_floorFadeFar;        

uniform bool u_curvatureAwareRelaxation; 

uniform bool u_useSegmentTracing;   
uniform float u_segmentFraction;    

uniform bool u_useAnalyticNormals;  

uniform float u_stepSafety;               
uniform bool u_curvatureNearOnly;         
uniform float u_curvatureNearK;           

uniform bool u_stepSafetyAuto;            
uniform float u_stepSafetyMin;            
uniform float u_stepSafetyMax;            
uniform float u_stepSafetyBandNear;       
uniform float u_stepSafetyBandFar;        

uniform bool u_conservativeHits;          

uniform bool u_integratorAuto;            
uniform float u_integratorSwitchDist;     

#define MAX_PALETTE_STOPS 8
uniform int u_colorMode; 
uniform int u_paletteId; 
uniform float u_colorIntensity; 
uniform float u_orbitTrapScale; 
uniform vec3 u_materialColor; 

uniform vec3 u_texColorBase;   
uniform vec3 u_texColorAccent; 

uniform bool u_texLayerColoring; 
uniform vec3 u_texA_colorBase;
uniform vec3 u_texA_colorAccent;
uniform vec3 u_texB_colorBase;
uniform vec3 u_texB_colorAccent;

uniform int u_useCustomPalette; 
uniform int  u_paletteStopCount; 
uniform float u_paletteStops[MAX_PALETTE_STOPS]; 
uniform vec3 u_paletteColors[MAX_PALETTE_STOPS];

uniform int u_paletteInterpMode;
uniform int u_paletteWrapMode;

uniform bool u_skipWhenEmpty;

uniform bool u_debugEnabled;
uniform int u_debugMode; 
uniform bool u_dbgBypassSierpinskiAlign;
uniform bool u_dbgBypassFractalRotation;

uniform float u_sierpinskiBase;

uniform float u_worldTile;       
uniform float u_worldThickness;  
uniform float u_worldWarp;       
uniform float u_worldDeScale;    
uniform float u_worldSegClamp;   
uniform float u_worldDetailStrength; 
uniform float u_worldDetailScale;    
uniform int   u_worldTexType;        
uniform float u_worldTexScale;
uniform float u_worldTexColorStrength;
uniform float u_worldTexBumpStrength;
uniform float u_worldTexSpecStrength;

uniform int   u_worldTexTypeB;
uniform float u_worldTexScaleB;
uniform float u_worldTexColorStrengthB;
uniform float u_worldTexBumpStrengthB;
uniform float u_worldTexSpecStrengthB;
uniform int   u_worldTexBlendMode;   
uniform float u_worldTexBlendAlphaColor; 
uniform float u_worldTexBlendAlphaBump;  
uniform float u_worldTexBlendAlphaSpec;  

uniform int   u_worldFbmOctaves;     
uniform float u_worldFbmLacunarity;  
uniform float u_worldFbmGain;        
uniform float u_worldFbmSeed;        

uniform float u_worldTexAAStrength;  

uniform bool  u_worldUseDEC;
uniform bool  u_worldTexAutoAtten;   

uniform float u_texWarpStrength; 
uniform float u_texWarpScale;    
uniform int   u_texWarpOctaves;  
uniform int   u_texWarpType;     

uniform float u_texAnisoFactor;  
uniform int   u_texAnisoAxis;    

uniform bool  u_texLODEnabled;   
uniform int   u_texDerivOctDrop; 
uniform int   u_texDerivMinOct;  
uniform int   u_texWarpOctDrop;  
uniform float u_texLODBumpFactor; 
uniform float u_texLODSpecFactor; 

uniform bool  u_texTop2;         
uniform bool  u_texFastBump;     
uniform float u_texTriMinWeight; 
uniform float u_texTriHyst;      

uniform float u_texDerivAggression; 
uniform float u_texBumpDerivFade;   
uniform float u_texSpecDerivFade;   
uniform float u_texRoughFadeK;      

uniform float u_texFadeNear;  
uniform float u_texFadeFar;

uniform float u_truchetRadius;   
uniform int   u_truchetShape;    
uniform int   u_truchetVariant;  
uniform bool  u_truchetSmooth;   
uniform float u_truchetSmoothK;  

uniform bool  u_truchetPortalFast;
uniform float u_truchetFastMargin; 
uniform float u_truchetFastK;      
uniform float u_truchetFastMinDist; 

uniform bool  u_truchetMirrorJoins;

uniform float u_truchetSleeveScale; 
uniform float u_truchetLipScale;    

uniform bool  u_truchetJoinRing;
uniform float u_truchetJoinRingK;

uniform int   u_worldTruchetRotate;  
uniform float u_worldTruchetWidth;   
uniform float u_worldTruchetDensity; 

uniform float u_hexFoldFreq;         
uniform float u_hexContrast;         

uniform bool  u_texturesEnabled;
uniform int   u_texSpaceMode; 

uniform int   u_aoMaxSamples;      
uniform float u_shadowEarlyExit;   
uniform float u_shadowStepClamp;   

uniform bool u_fastNormals;        
uniform bool u_fastShadows;        
uniform bool u_fastAO;             

uniform float u_aoFallbackStrength; 

uniform vec3  u_decOffset;      

uniform float u_postExposure;   
uniform float u_postContrast;   
uniform float u_postSaturation; 
uniform float u_postGamma;      
uniform float u_vignetteStrength; 
uniform float u_vignetteSoftness; 
uniform int   u_toneMapper;     

#define FT_PRIMITIVES 0
#define FT_MENGER     1
#define FT_BULB       2
#define FT_SIERPINSKI 3
#define FT_MANDELBOX  4
#define FT_WORLD      5
#define FT_TRUCHET    6
#define FT_DEC        7
vec3 repeatCentered(vec3 p, float period) {
  return mod(p + period * 0.5, period) - period * 0.5;
}
vec2 repeatCentered(vec2 p, float period) { 
  return mod(p + period * 0.5, period) - period * 0.5;
}

vec3 warpDomain(vec3 p, float warp) {
  if (warp <= 0.0) return p;
  vec3 s1 = sin(p.zxy * 1.37);
  vec3 s2 = sin(p.yzx * 2.11);
  return p + warp * (s1 * 0.6 + s2 * 0.4);
}

float gyroidDE(vec3 p, float thickness) {
  float sx = sin(p.x), cx = cos(p.x);
  float sy = sin(p.y), cy = cos(p.y);
  float sz = sin(p.z), cz = cos(p.z);

  float f = sx * cy + sy * cz + sz * cx;
  
  vec3 g;
  g.x = cx * cy - sz * sx;          
  g.y = -sx * sy + cy * cz;          
  g.z = -sy * sz + cz * cx;          
  
  float gl = max(0.60, length(g));   
  float d = (abs(f) - thickness) / gl;
  return d;
}

float deAmazingSurf(vec3 p, float tile, float thickness, float warp, float scale, out float trapOut) {
  
  vec3 q = p;
  q.xz = repeatCentered(q.xz, tile);
  q = warpDomain(q, warp);
  q /= max(0.0001, scale);

  float d = gyroidDE(q, thickness);

  
  float sx = sin(q.x), cx = cos(q.x);
  float sy = sin(q.y), cy = cos(q.y);
  float sz = sin(q.z), cz = cos(q.z);
  float f = abs(sx * cy + sy * cz + sz * cx);
  trapOut = clamp(f * 0.8 + abs(q.y) * 0.1, 0.0, 2.0);

  return d * scale; 
}

float hashT(vec3 p) {
  p += vec3(u_worldFbmSeed);
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

float noiseT(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float n000 = hashT(i + vec3(0,0,0));
  float n100 = hashT(i + vec3(1,0,0));
  float n010 = hashT(i + vec3(0,1,0));
  float n110 = hashT(i + vec3(1,1,0));
  float n001 = hashT(i + vec3(0,0,1));
  float n101 = hashT(i + vec3(1,0,1));
  float n011 = hashT(i + vec3(0,1,1));
  float n111 = hashT(i + vec3(1,1,1));
  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);
  float nxy0 = mix(nx00, nx10, f.y);
  float nxy1 = mix(nx01, nx11, f.y);
  return mix(nxy0, nxy1, f.z);
}

float fbmT(vec3 p) {
  float gain = clamp(u_worldFbmGain, 0.1, 0.95);
  float lac  = clamp(u_worldFbmLacunarity, 1.1, 4.0);
  int   oct  = clamp(u_worldFbmOctaves, 1, 8);
  float a = 0.5;
  float s = 0.0;
  vec3 q = p;
  for (int i = 0; i < 8; ++i) {
    if (i >= oct) break;
    s += a * noiseT(q);
    q *= lac;
    a *= gain;
  }
  return s;
}

float fbmOct(vec3 p, int oct, float lac, float gain) {
  float a = 0.5;
  float s = 0.0;
  vec3 q = p;
  for (int i = 0; i < 8; ++i) {
    if (i >= oct) break;
    s += a * noiseT(q);
    q *= lac;
    a *= gain;
  }
  return s;
}

float fbmRidged(vec3 p, int oct, float lac, float gain) {
  float a = 0.5;
  float s = 0.0;
  vec3 q = p;
  for (int i = 0; i < 8; ++i) {
    if (i >= oct) break;
    float n = noiseT(q);
    n = 1.0 - abs(n * 2.0 - 1.0); 
    s += a * n;
    q *= lac;
    a *= gain;
  }
  return s;
}

vec3 applyTextureWarp(vec3 p) {
  #ifdef DISABLE_TEX_WARP
    return p;
  #endif
  if (u_texWarpStrength <= 0.0) return p;
  float s = max(0.05, u_texWarpScale);
  int   o = clamp(u_texWarpOctaves, 1, 6);
  float lac = 2.0;
  float gain = 0.5;
  float x, y, z;
  if (u_texWarpType == 2) {
    x = fbmRidged(p.xyz * s, o, lac, gain);
    y = fbmRidged(p.yzx * s, o, lac, gain);
    z = fbmRidged(p.zxy * s, o, lac, gain);
  } else {
    x = fbmOct(p.xyz * s, o, lac, gain);
    y = fbmOct(p.yzx * s, o, lac, gain);
    z = fbmOct(p.zxy * s, o, lac, gain);
  }
  vec3 w = vec3(x, y, z);
  
  w = (w - 0.5) * 2.0;
  return p + w * clamp(u_texWarpStrength, 0.0, 2.0);
}

vec3 applyTextureAnisotropy(vec3 p) {
  float f = clamp(u_texAnisoFactor, 0.05, 4.0);
  if (abs(f - 1.0) < 1e-6) return p;
  if (u_texAnisoAxis == 0) return vec3(p.x * f, p.y, p.z);
  if (u_texAnisoAxis == 1) return vec3(p.x, p.y * f, p.z);
  return vec3(p.x, p.y, p.z * f);
}

vec3 applyTextureWarpLOD(vec3 p, float lodGrad) {
  #ifdef DISABLE_TEX_WARP
    return p;
  #endif
  if (u_texWarpStrength <= 0.0) return p;
  float s = max(0.05, u_texWarpScale);
  int   oBase = clamp(u_texWarpOctaves, 1, 6);
  int   oDrop = int(floor(clamp(lodGrad, 0.0, 1.0) * float(max(0, u_texWarpOctDrop))));
  int   o = max(1, oBase - oDrop);
  float lac = 2.0;
  float gain = 0.5;
  float x, y, z;
  if (u_texWarpType == 2) {
    x = fbmRidged(p.xyz * s, o, lac, gain);
    y = fbmRidged(p.yzx * s, o, lac, gain);
    z = fbmRidged(p.zxy * s, o, lac, gain);
  } else {
    x = fbmOct(p.xyz * s, o, lac, gain);
    y = fbmOct(p.yzx * s, o, lac, gain);
    z = fbmOct(p.zxy * s, o, lac, gain);
  }
  vec3 w = vec3(x, y, z);
  w = (w - 0.5) * 2.0;
  return p + w * clamp(u_texWarpStrength, 0.0, 2.0);
}

vec3 triWeights(vec3 n) {
  n = abs(n) + 1e-4;
  return n / (n.x + n.y + n.z);
}

vec3 triWeightsOpt(vec3 n) {
  vec3 a = abs(n) + 1e-4;
  vec3 w = a / (a.x + a.y + a.z);
  if (u_texTop2) {
    
    
    
    float tEff = clamp(u_texTriMinWeight + max(0.0, u_texTriHyst), 0.0, 0.3);
    if (w.x < w.y && w.x < w.z && w.x < tEff) { w = vec3(0.0, w.y, w.z); }
    else if (w.y < w.x && w.y < w.z && w.y < tEff) { w = vec3(w.x, 0.0, w.z); }
    else if (w.z < w.x && w.z < w.y && w.z < tEff) { w = vec3(w.x, w.y, 0.0); }
    float s = w.x + w.y + w.z;
    if (s > 0.0) w /= s;
  }
  return w;
}

float triplanarFbmDetail(vec3 p, vec3 n, float scale) {
  vec3 w = triWeightsOpt(n);
  float s = max(0.05, scale);
  
  const float W_EPS = 0.06;
  float acc = 0.0;
  float ww = 0.0;
  if (w.x > W_EPS) { float x = fbmT(p.yzx * s); acc += x * w.x; ww += w.x; }
  if (w.y > W_EPS) { float y = fbmT(p.zxy * s); acc += y * w.y; ww += w.y; }
  if (w.z > W_EPS) { float z = fbmT(p.xyz * s); acc += z * w.z; ww += w.z; }
  
  return (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
}

float hashT2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

float valueNoise2D(vec2 q) {
  vec2 i = floor(q);
  vec2 f = fract(q);
  f = f * f * (3.0 - 2.0 * f);
  float n00 = hashT2(i + vec2(0.0, 0.0));
  float n10 = hashT2(i + vec2(1.0, 0.0));
  float n01 = hashT2(i + vec2(0.0, 1.0));
  float n11 = hashT2(i + vec2(1.0, 1.0));
  float nx0 = mix(n00, n10, f.x);
  float nx1 = mix(n01, n11, f.x);
  return mix(nx0, nx1, f.y);
}

float truchet2D(vec2 uv) {
  uv *= max(0.1, u_worldTruchetDensity);
  vec2 id = floor(uv);
  vec2 f = fract(uv);
  float r = step(0.5, hashT2(id + u_worldFbmSeed));
  if (u_worldTruchetRotate == 1) {
    if (r > 0.5) f = f.yx;
  }
  float d = abs(f.x - f.y);
  float w = clamp(u_worldTruchetWidth, 0.02, 0.6);
  return smoothstep(w, 0.0, d);
}

float triplanarTruchet(vec3 p, vec3 n, float scale) {
  vec3 w = triWeightsOpt(n);
  float s = max(0.05, scale);
  const float W_EPS = 0.06;
  float acc = 0.0; float ww = 0.0;
  if (w.x > W_EPS) { float x = truchet2D(p.yz * s); acc += x * w.x; ww += w.x; }
  if (w.y > W_EPS) { float y = truchet2D(p.zx * s); acc += y * w.y; ww += w.y; }
  if (w.z > W_EPS) { float z = truchet2D(p.xy * s); acc += z * w.z; ww += w.z; }
  return (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
}

uniform float u_hexSeed; 

float hexHeightMap(vec2 p) {
  
  vec2 h = vec2(p.x + p.y * 0.57735, p.y * 1.1547);
  vec2 fh = floor(h);
  vec2 f = fract(h);
  h = fh;
  float c = fract((h.x + h.y) / 3.0);
  h = (c < 0.666) ? ((c < 0.333) ? h : h + 1.0) : (h + step(f.yx, f));

  
  p -= vec2(h.x - h.y * 0.5, h.y * 0.8660254);

  
  c = fract(cos(dot(h, vec2(41.0, 289.0)) + u_hexSeed) * 43758.5453);
  p -= p * step(c, 0.5) * 2.0; 

  
  p -= vec2(-1.0, 0.0);
  float d2 = dot(p, p);
  p -= vec2(1.5, 0.8660254);
  d2 = min(d2, dot(p, p));
  p -= vec2(0.0, -1.73205);
  d2 = min(d2, dot(p, p));

  return sqrt(d2);
}

float hexTruchet2D(vec2 uv) {
  
  
  float c = hexHeightMap(uv);
  const float TAU = 6.28318530718;
  float f = max(0.25, u_hexFoldFreq);
  
  c = cos(c * TAU * f) + cos(c * TAU * 2.0 * f);
  
  float k = clamp(u_hexContrast, 0.25, 2.5);
  return clamp(c * (0.6 * k) + 0.5, 0.0, 1.0);
}

float triplanarHexTruchet(vec3 p, vec3 n, float scale) {
  vec3 w = triWeightsOpt(n);
  float s = max(0.05, scale);
  const float W_EPS = 0.06;
  float acc = 0.0; float ww = 0.0;
  if (w.x > W_EPS) { acc += hexTruchet2D(p.yz * s) * w.x; ww += w.x; }
  if (w.y > W_EPS) { acc += hexTruchet2D(p.zx * s) * w.y; ww += w.y; }
  if (w.z > W_EPS) { acc += hexTruchet2D(p.xy * s) * w.z; ww += w.z; }
  return (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
}

float checker2D(vec2 uv) {
  
  const float PI = 3.14159265359;
  float v = sin(PI * uv.x) * sin(PI * uv.y);
  
  return 0.5 + 0.5 * sign(v + 1e-6);
}

float triplanarChecker(vec3 p, vec3 n, float scale) {
  vec3 w = triWeightsOpt(n);
  float s = max(0.05, scale);
  const float W_EPS = 0.06;
  float acc = 0.0; float ww = 0.0;
  if (w.x > W_EPS) { acc += checker2D(p.yz * s) * w.x; ww += w.x; }
  if (w.y > W_EPS) { acc += checker2D(p.zx * s) * w.y; ww += w.y; }
  if (w.z > W_EPS) { acc += checker2D(p.xy * s) * w.z; ww += w.z; }
  return (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
}

float fbm2D(vec2 p, int oct, float lac, float gain) {
  float a = 0.5;
  float s = 0.0;
  vec2 q = p;
  for (int i = 0; i < 8; ++i) {
    if (i >= oct) break;
    s += a * valueNoise2D(q);
    q *= lac;
    a *= gain;
  }
  return s;
}

float fbmRidged2D(vec2 p, int oct, float lac, float gain) {
  float a = 0.5;
  float s = 0.0;
  vec2 q = p;
  for (int i = 0; i < 8; ++i) {
    if (i >= oct) break;
    float n = valueNoise2D(q);
    n = 1.0 - abs(n * 2.0 - 1.0);
    s += a * n;
    q *= lac;
    a *= gain;
  }
  return s;
}

vec2 applyTextureWarp2D(vec2 uv) {
  if (u_texWarpStrength <= 0.0) return uv;
  float s = max(0.05, u_texWarpScale);
  int   o = clamp(u_texWarpOctaves, 1, 6);
  float lac = 2.0;
  float gain = 0.5;
  float x, y;
  if (u_texWarpType == 2) {
    x = fbmRidged2D(uv.xy * s, o, lac, gain);
    y = fbmRidged2D(uv.yx * s, o, lac, gain);
  } else {
    x = fbm2D(uv.xy * s, o, lac, gain);
    y = fbm2D(uv.yx * s, o, lac, gain);
  }
  vec2 w = vec2(x, y);
  w = (w - 0.5) * 2.0;
  return uv + w * clamp(u_texWarpStrength, 0.0, 2.0);
}

float floorTextureValue2D(vec3 p, float scale, int type) {
  float s = max(0.05, scale);
  vec2 uv = p.xz * s;
  
  if (u_texAnisoFactor != 1.0) {
    float f = clamp(u_texAnisoFactor, 0.05, 4.0);
    if (u_texAnisoAxis == 0) uv.x *= f; 
    else if (u_texAnisoAxis == 2) uv.y *= f; 
  }
  
  if (!u_floorIgnoreWarp) {
    uv = applyTextureWarp2D(uv);
  }

  float aa = clamp(u_worldTexAAStrength, 0.0, 1.0);
  float fw = length(fwidth(uv));
  float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));

  if (type == 1) {
    int oct = clamp(u_worldFbmOctaves, 1, 8);
    float t = fbm2D(uv, oct, clamp(u_worldFbmLacunarity, 1.1, 4.0), clamp(u_worldFbmGain, 0.1, 0.95));
    return mix(0.5, t, atten);
  }
  if (type == 2) {
    float t = valueNoise2D(uv);
    return mix(0.5, t, atten);
  }
  if (type == 3) {
    float t = truchet2D(uv);
    return mix(0.5, t, atten);
  }
  if (type == 4) {
    float t = hexTruchet2D(uv);
    return mix(0.5, t, atten);
  }
  if (type == 5) {
    float t = checker2D(uv);
    return mix(0.5, t, atten);
  }
  return 0.0;
}

float worldTextureValueNoWarp(vec3 p, vec3 n, float scale, int type) {
  
  p = applyTextureAnisotropy(p);
  float aa = clamp(u_worldTexAAStrength, 0.0, 1.0);
  vec3 w = triWeightsOpt(n);
  const float W_EPS = 0.06;
  float s = max(0.05, scale);
  if (type == 1) {
    float acc = 0.0; float ww = 0.0;
    if (w.x > W_EPS) { acc += fbmT(p.yzx * s) * w.x; ww += w.x; }
    if (w.y > W_EPS) { acc += fbmT(p.zxy * s) * w.y; ww += w.y; }
    if (w.z > W_EPS) { acc += fbmT(p.xyz * s) * w.z; ww += w.z; }
    float t = (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 2) {
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float acc = 0.0; float ww = 0.0;
    if (w.x > W_EPS) { acc += valueNoise2D(ux) * w.x; ww += w.x; }
    if (w.y > W_EPS) { acc += valueNoise2D(uy) * w.y; ww += w.y; }
    if (w.z > W_EPS) { acc += valueNoise2D(uz) * w.z; ww += w.z; }
    float t = (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 3) {
    float t = triplanarTruchet(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 4) {
    float t = triplanarHexTruchet(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 5) {
    float t = triplanarChecker(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  return 0.0;
}

float worldTextureValue(vec3 p, vec3 n, float scale, int type) {
  
  p = applyTextureAnisotropy(p);
  float s = max(0.05, scale);
  
  vec2 ux0 = p.yz * s; vec2 uy0 = p.zx * s; vec2 uz0 = p.xy * s;
  float fw0 = max(max(length(fwidth(ux0)), length(fwidth(uy0))), length(fwidth(uz0)));
  float lodGrad = (u_texLODEnabled ? smoothstep(0.6, 2.0, fw0) : 0.0);
  
  if (u_texLODEnabled) p = applyTextureWarpLOD(p, lodGrad * max(0.0, u_texDerivAggression)); else p = applyTextureWarp(p);
  float aa = clamp(u_worldTexAAStrength, 0.0, 1.0);
  vec3 w = triWeightsOpt(n);
  const float W_EPS = 0.06;
  if (type == 1) {
    int oBase = clamp(u_worldFbmOctaves, 1, 8);
    int oDrop = (u_texLODEnabled ? int(floor(lodGrad * max(0.0, u_texDerivAggression) * float(max(0, u_texDerivOctDrop)))) : 0);
    int oMin  = clamp(u_texDerivMinOct, 1, 8);
    int oct   = max(oMin, oBase - oDrop);
    float lac = clamp(u_worldFbmLacunarity, 1.1, 4.0);
    float gai = clamp(u_worldFbmGain, 0.1, 0.95);
    float acc = 0.0; float ww = 0.0;
    if (w.x > W_EPS) { acc += fbmOct(p.yzx * s, oct, lac, gai) * w.x; ww += w.x; }
    if (w.y > W_EPS) { acc += fbmOct(p.zxy * s, oct, lac, gai) * w.y; ww += w.y; }
    if (w.z > W_EPS) { acc += fbmOct(p.xyz * s, oct, lac, gai) * w.z; ww += w.z; }
    float t = (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 2) {
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float acc = 0.0; float ww = 0.0;
    if (w.x > W_EPS) { acc += valueNoise2D(ux) * w.x; ww += w.x; }
    if (w.y > W_EPS) { acc += valueNoise2D(uy) * w.y; ww += w.y; }
    if (w.z > W_EPS) { acc += valueNoise2D(uz) * w.z; ww += w.z; }
    float t = (ww > 0.0) ? (acc * (1.0 / ww)) : 0.0;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 3) {
    float t = triplanarTruchet(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 4) {
    float t = triplanarHexTruchet(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  if (type == 5) {
    float t = triplanarChecker(p, n, scale);
    vec2 ux = p.yz * s; vec2 uy = p.zx * s; vec2 uz = p.xy * s;
    float fw = max(max(length(fwidth(ux)), length(fwidth(uy))), length(fwidth(uz)));
    float atten = 1.0 - smoothstep(0.6, 2.0, fw * (0.5 + 2.0 * aa));
    return mix(0.5, t, atten);
  }
  return 0.0;
}

mat2 tr_rot2(float a){ float c=cos(a), s=sin(a); return mat2(c,-s,s,c); }

float sdBox2(vec2 p, float b){
  vec2 d = abs(p) - vec2(b);
  return min(max(d.x, d.y), 0.0) + length(max(d, 0.0));
}

float tr_tube(vec2 p, float sc, float rad){
  vec2 a = abs(p);
  if (u_truchetShape == 0) {
    return length(a) - rad; 
  } else if (u_truchetShape == 1) {
    return sdBox2(a, rad);  
  } else if (u_truchetShape == 2) {
    
    return smax(a.x, a.y, 0.015) - rad;
  } else {
    
    return max(max(a.x, a.y), (a.x + a.y) * sc) - rad; 
  }
}

vec4 tr_torTube(vec3 p, float rad){
  const float scOct = 0.7071; 

  float tb = tr_tube(abs(vec2(length(p.xy) - 0.5, p.z)), 0.75, rad);

  
  float innerTb = 1e5; 
  const float aNum = 12.0; 

  
  p.xy = tr_rot2(PI * 0.5) * p.xy; 
  float a = atan(p.y, p.x);
  float ia = floor(a / TAU * aNum) + 0.5; 
  p.xy = tr_rot2(ia * TAU / aNum) * p.xy;
  p.x -= 0.5; 
  vec3 q = abs(p);

  
  float sleeveK = clamp(u_truchetSleeveScale, 0.5, 1.2);
  float band = max(tr_tube(q.xz, 0.75, rad + 0.0075 * sleeveK), q.y - (0.06 * sleeveK));
  vec2 peg = vec2(tr_tube(q.xy, 0.64, 0.0425), tr_tube(q.yz, 0.64, 0.0425));

  if (mod(ia + 1.0, 3.0) > 2.0) {
    
    band = min(band, max(tr_tube(q.xz, 0.6, rad + 0.015 * sleeveK), q.y - (0.04 * sleeveK)));
    band = min(band, max(tr_tube(q.xz, 0.6, rad + 0.025 * sleeveK), q.y - (0.0133 * sleeveK)));
  } else {
    
    float hole = min(peg.x, peg.y);
    float lipBase = (u_truchetShape == 3 ? 0.0075 : 0.02);
    float lip = rad + lipBase * clamp(u_truchetLipScale, 0.5, 1.5);
    band = min(band, min(max(peg.x, q.z - lip), max(peg.y, q.x - lip)));
    band = max(band, -(hole + 0.015));
    tb   = max(tb,   -(hole + 0.015)); 
    innerTb = length(q) - rad + 0.01;   
  }

  return vec4(tb, band, innerTb, ia);
}

vec4 tr_straightTube(vec3 p, float rad){
  float sleeveK = clamp(u_truchetSleeveScale, 0.5, 1.2);
  float tb = tr_tube(abs(p.xy), 0.75, rad);
  float innerTb = 1e5;

  const float aNum = 1.0; 
  float ia = floor(p.z * 3.0 * aNum);
  float opz = mod(p.z + 1.0 / aNum / 3.0, 1.0 / aNum);
  p.z = mod(p.z, 1.0 / aNum / 3.0) - 0.5 / aNum / 3.0;
  vec3 q = abs(p);

  float band = max(tb - (0.0075 * sleeveK), q.z - (0.06 * sleeveK));
  vec2 peg = vec2(tr_tube(q.xz, 0.64, 0.0425), tr_tube(q.yz, 0.64, 0.0425));

  if (opz > 2.0 / aNum / 3.0) {
    band = min(band, max(tr_tube(q.xy, 0.6, rad + 0.015 * sleeveK), q.z - (0.04 * sleeveK)));
    band = min(band, max(tr_tube(q.xy, 0.6, rad + 0.025 * sleeveK), q.z - (0.0133 * sleeveK)));
  } else {
    float hole = min(peg.x, peg.y);
    float lipBase = (u_truchetShape == 3 ? 0.0075 : 0.02);
    float lip = rad + lipBase * clamp(u_truchetLipScale, 0.5, 1.5);
    band = min(band, min(max(peg.x, q.y - lip), max(peg.y, q.x - lip)));
    band = max(band, -(hole + 0.015));
    tb   = max(tb,   -(hole + 0.015));
    innerTb = length(q) - rad + 0.01;
  }

  return vec4(tb, band, innerTb, float(ia));
}

vec4 tr_torTubeTest(vec3 p){
  vec2 v = vec2(length(p.xy) - 0.5, p.z);
  return vec4(p, dot(v, v));
}
vec4 tr_straightTubeTest(vec3 p){
  vec2 v = p.xy;
  return vec4(p, dot(v, v));
}

float deTruchetPipesUnit(vec3 p, out float trap, bool allowFast){
  
  float rnd = fract(sin(dot(floor(p + vec3(111.0, 73.0, 27.0)), vec3(7.63, 157.31, 113.97))) * 43758.5453);
  float rnd2 = fract(rnd * 41739.7613 + 0.131);

  
  vec3 q = fract(p) - 0.5;

  
  if (rnd > 0.833) q = q.xzy; else if (rnd > 0.666) q = q.yxz; else if (rnd > 0.5) q = q.yzx; else if (rnd > 0.333) q = q.zxy; else if (rnd > 0.166) q = q.zyx;

  
  vec4 tb1 = tr_torTubeTest(vec3(q.xy + 0.5, q.z));
  vec4 tb2, tb3;
  if (rnd2 > 0.66 || u_truchetVariant == 1) {
    tb2 = tr_torTubeTest(vec3(q.yz - 0.5, q.x));
    tb3 = tr_torTubeTest(vec3(q.xz - vec2(0.5, -0.5), q.y));
  } else if (u_truchetVariant == 2) {
    
    tb2 = tr_straightTubeTest(vec3(q.xy - 0.5, q.z));
    tb3 = tr_straightTubeTest(q);
  } else {
    tb2 = tr_torTubeTest(vec3(q.xy - 0.5, q.z));
    tb3 = tr_straightTubeTest(q);
  }

  
  vec3 op = (tb1.w < tb2.w && tb1.w < tb3.w) ? tb1.xyz : (tb2.w < tb3.w ? tb2.xyz : tb3.xyz);

  
  float rad = clamp(u_truchetRadius, 0.03, 0.16);

  
  
  
  
  if (allowFast && u_truchetPortalFast) {
    float tbQuick;
    bool useStraight = ((rnd2 <= 0.66 && tb3.w < tb1.w && tb3.w < tb2.w) || u_truchetVariant == 2);
    if (useStraight) {
      tbQuick = tr_tube(abs(op.xy), 0.75, rad);
    } else {
      tbQuick = tr_tube(abs(vec2(length(op.xy) - 0.5, op.z)), 0.75, rad);
    }
    
    float margin = clamp(u_truchetFastMargin, 0.015, 0.06);
    float kGate = clamp(u_truchetFastK, 1.0, 8.0);
    
    if (tbQuick > margin * kGate) {
      trap = fract(rnd * 3.17); 
      return tbQuick - margin;
    }
  }

  
  
  
  
  bool chooseStraight = ((rnd2 <= 0.66 && tb3.w < tb1.w && tb3.w < tb2.w) || u_truchetVariant == 2);
  float tbMain = chooseStraight
    ? tr_tube(abs(op.xy), 0.75, rad)
    : tr_tube(abs(vec2(length(op.xy) - 0.5, op.z)), 0.75, rad);

  bool detailLikely = true;
  if (chooseStraight) {
    
    
    
    float zAbs = abs(op.z);
    float rimDev = abs(length(op.xy) - rad);
    detailLikely = (zAbs < 0.18) || (rimDev < 0.10);
  } else {
    
    
    float minorAbs = abs(op.z);
    float ringDev = abs(length(op.xy) - 0.5);
    detailLikely = (minorAbs < 0.16) || (ringDev < (rad + 0.11));
  }

  if (!detailLikely) {
    
    trap = fract(rnd * 3.17);
    return tbMain;
  }

  vec4 dec = chooseStraight ? tr_straightTube(op, rad) : tr_torTube(op, rad);
  float d;
  float dMain;
  {
    
    if (u_truchetSmooth) {
      float kScale = clamp(u_truchetSmoothK, 0.0, 0.5);
      float smK = clamp(rad * (0.02 + kScale), 0.003, 0.06);
      float tbBand = smin(dec.x, dec.y, smK);
      dMain = min(tbBand, dec.z);
    } else {
      dMain = min(min(dec.x, dec.y), dec.z);
    }
  }

  
  if (u_truchetJoinRing) {
    float k = clamp(u_truchetJoinRingK, 0.5, 1.8);
    
    vec3 opT = (tb1.w < tb2.w) ? tb1.xyz : tb2.xyz;
    vec3 opS = tb3.xyz;
    
    float rrT = abs(length(opT.xy) - 0.5);
    float wRT = 0.035 * k;   
    float hZT = 0.030 * k;   
    float dT = 1e5;
    if (rrT < (wRT + 0.12)) {
      dT = max(rrT - wRT, abs(opT.z) - hZT);
    }
    
    float rS = abs(length(opS.xy) - clamp(u_truchetRadius, 0.03, 0.16));
    float wRS = max(0.020 * k, 0.75 * wRT);
    float hZS = 0.028 * k;
    float dS = 1e5;
    if (abs(opS.z) < (hZS + 0.12)) {
      dS = max(rS - wRS, abs(opS.z) - hZS);
    }
    float dRing = min(dT, dS);
    d = min(dMain, dRing);
  } else {
    d = dMain;
  }

  
  float seg = abs(dec.w);
  trap = fract(seg * 0.173 + rnd * 3.17);
  return d;
}

float deTruchetPipesWorld(vec3 p, out float trap){
  float tile = max(0.5, u_worldTile);
  
  vec3 q = rotate3D(p, u_rotation);
  
  q /= tile;
  
  bool allowFast = (length(p - u_cameraPos) > max(0.0, u_truchetFastMinDist));
  float d = deTruchetPipesUnit(q, trap, allowFast);
  
  return d * tile * clamp(u_worldDeScale, 0.5, 1.0);
}

float deTruchetPipesUnitShadow(vec3 p, out float trap){
  
  float rnd = fract(sin(dot(floor(p + vec3(111.0, 73.0, 27.0)), vec3(7.63, 157.31, 113.97))) * 43758.5453);
  float rnd2 = fract(rnd * 41739.7613 + 0.131);
  vec3 q = fract(p) - 0.5;
  if (rnd > 0.833) q = q.xzy; else if (rnd > 0.666) q = q.yxz; else if (rnd > 0.5) q = q.yzx; else if (rnd > 0.333) q = q.zxy; else if (rnd > 0.166) q = q.zyx;
  vec4 tb1 = tr_torTubeTest(vec3(q.xy + 0.5, q.z));
  vec4 tb2, tb3;
  if (rnd2 > 0.66 || u_truchetVariant == 1) {
    tb2 = tr_torTubeTest(vec3(q.yz - 0.5, q.x));
    tb3 = tr_torTubeTest(vec3(q.xz - vec2(0.5, -0.5), q.y));
  } else if (u_truchetVariant == 2) {
    tb2 = tr_straightTubeTest(vec3(q.xy - 0.5, q.z));
    tb3 = tr_straightTubeTest(q);
  } else {
    tb2 = tr_torTubeTest(vec3(q.xy - 0.5, q.z));
    tb3 = tr_straightTubeTest(q);
  }
  vec3 op = (tb1.w < tb2.w && tb1.w < tb3.w) ? tb1.xyz : (tb2.w < tb3.w ? tb2.xyz : tb3.xyz);
  float rad = clamp(u_truchetRadius, 0.03, 0.16);
  bool chooseStraight = ((rnd2 <= 0.66 && tb3.w < tb1.w && tb3.w < tb2.w) || u_truchetVariant == 2);
  float tbQuick = chooseStraight ? tr_tube(abs(op.xy), 0.75, rad)
                                : tr_tube(abs(vec2(length(op.xy) - 0.5, op.z)), 0.75, rad);
  
  
  float base = clamp(u_truchetFastMargin * 1.35, 0.025, 0.09);
  float extra = 0.0;
  if (chooseStraight) {
    float zAbs = abs(op.z);
    float rimDev = abs(length(op.xy) - rad);
    extra = (step(zAbs, 0.18) + step(rimDev, 0.10)) * 0.0075; 
  } else {
    float minorAbs = abs(op.z);
    float ringDev = abs(length(op.xy) - 0.5);
    extra = (step(minorAbs, 0.16) + step(ringDev, rad + 0.11)) * 0.0075;
  }
  float margin = min(0.10, base + extra + rad * 0.06);
  trap = fract(rnd * 3.17);
  return tbQuick - margin;
}

float deTruchetPipesWorldShadow(vec3 p, out float trap){
  float tile = max(0.5, u_worldTile);
  vec3 q = rotate3D(p, u_rotation) / tile;
  float d = deTruchetPipesUnitShadow(q, trap);
  return d * tile * clamp(u_worldDeScale, 0.5, 1.0);
}

float hash(vec2 p) {
  
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

float hash3D(vec3 p) {
  return fract(sin(dot(p, vec3(12.9898, 78.233, 45.543))) * 43758.5453);
}

float interleavedGradientNoise(vec2 screenPos) {
  vec3 magic = vec3(0.06711056, 0.00583715, 52.9829189);
  return fract(magic.z * fract(dot(screenPos, magic.xy)));
}

float blueNoiseHash(vec2 screenPos) {
  vec2 p = floor(screenPos / max(1.0, u_blueNoiseScale));
  if (u_blueNoiseTemporalJitter) {
    p += vec2(fract(u_time * 0.0719), fract(u_time * 0.0463));
  }
  return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453);
}

float ditherNearSurface(float dist, float eps) {
  if (!u_enableDithering) return 0.0;
  float n = u_useBlueNoise ? blueNoiseHash(gl_FragCoord.xy) : interleavedGradientNoise(gl_FragCoord.xy);
  float edge = 1.0 - smoothstep(eps * 2.0, eps * 8.0, dist);
  return n * u_ditheringStrength * eps * edge;
}

float gradientNoise3D(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);

  
  f = f * f * (3.0 - 2.0 * f);

  
  float n000 = hash3D(i + vec3(0.0, 0.0, 0.0));
  float n100 = hash3D(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash3D(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash3D(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash3D(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash3D(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash3D(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash3D(i + vec3(1.0, 1.0, 1.0));

  
  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);

  float nxy0 = mix(nx00, nx10, f.y);
  float nxy1 = mix(nx01, nx11, f.y);

  return mix(nxy0, nxy1, f.z);
}

vec3 sampleCustomPalette(float t) {
    float x;
    if (u_paletteWrapMode == 1) { 
        x = fract(t);
    } else if (u_paletteWrapMode == 2) { 
        float f = fract(t);
        x = 1.0 - abs(1.0 - 2.0 * f);
    } else { 
        x = clamp(t, 0.0, 1.0);
    }
    int count = u_paletteStopCount;
    if (count <= 0) return vec3(x); 
    if (count == 1) return u_paletteColors[0];

    
    int idx = 0;
    for (int j = 1; j < MAX_PALETTE_STOPS; ++j) {
        if (j >= count) { idx = max(0, count - 2); break; }
        if (x < u_paletteStops[j]) { idx = j - 1; break; }
        idx = max(0, count - 2);
    }
    float t0 = u_paletteStops[idx];
    float t1 = u_paletteStops[idx + 1];
    vec3  c0 = u_paletteColors[idx];
    vec3  c1 = u_paletteColors[idx + 1];
    float f = (t1 > t0) ? clamp((x - t0) / (t1 - t0), 0.0, 1.0) : 0.0;
    if (u_paletteInterpMode == 1) {
        
        f = 0.5 - 0.5 * cos(3.14159265 * f);
    }
    return mix(c0, c1, f);
}
vec3 getPaletteColor(float t, int paletteId) {
    if (u_useCustomPalette == 1 || paletteId < 0) {
        return sampleCustomPalette(t);
    }
    if (paletteId == 0) return paletteDeepOcean(t);
    if (paletteId == 1) return paletteMoltenLava(t);
    if (paletteId == 2) return paletteElectric(t);
    if (paletteId == 3) return paletteOrganic(t);
    if (paletteId == 4) return paletteMonochrome(t);
    if (paletteId == 5) return paletteDeepAbyss(t);
    if (paletteId == 6) return paletteTropicalSea(t);
    return paletteDeepOcean(t);
}

vec3 rotateX(vec3 p, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return vec3(p.x, c * p.y - s * p.z, s * p.y + c * p.z);
}

vec3 rotateY(vec3 p, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return vec3(c * p.x - s * p.z, p.y, s * p.x + c * p.z);
}

vec3 rotateZ(vec3 p, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z);
}

vec3 rotate3D(vec3 p, vec3 angles) {
  p = rotateX(p, angles.x);
  p = rotateY(p, angles.y);
  p = rotateZ(p, angles.z);
  return p;
}

vec3 rotate3DInv(vec3 p, vec3 angles) {
  p = rotateZ(p, -angles.z);
  p = rotateY(p, -angles.y);
  p = rotateX(p, -angles.x);
  return p;
}

const float MB_NORM = 0.25;

vec3 mbToLocal(vec3 p) {
  vec3 rotated = rotate3D(p, u_rotation);
  return (rotated - u_zoomCenter) / u_fractalScale;
}
vec3 mbNormalToWorld(vec3 nLocal) {
  return rotate3DInv(nLocal, u_rotation);
}

vec3 mgToLocal(vec3 p) {
  vec3 rotated = rotate3D(p, u_rotation);
  return (rotated - u_zoomCenter) / u_fractalScale;
}
vec3 mgNormalToWorld(vec3 nLocal) {
  return rotate3DInv(nLocal, u_rotation);
}

vec3 mbxToLocal(vec3 p) {
  vec3 rotated = rotate3D(p, u_rotation);
  return (rotated - u_zoomCenter) / (u_fractalScale * MB_NORM);
}
vec3 mbxNormalToWorld(vec3 nLocal) {
  return rotate3DInv(nLocal, u_rotation);
}

mat3 sierpAlignFwd() { return rotationAlign(vec3(1.0, 1.0, 1.0), vec3(0.0, 1.0, 0.0)); }
mat3 sierpAlignInv() { return rotationAlign(vec3(0.0, 1.0, 0.0), vec3(1.0, 1.0, 1.0)); }

vec3 spToLocal(vec3 p) {
  vec3 q = p;
  if (!u_dbgBypassSierpinskiAlign) q = sierpAlignFwd() * q;
  if (!u_dbgBypassFractalRotation) q = rotate3D(q, u_rotation);
  return (q - u_zoomCenter) / u_fractalScale;
}
vec3 spDirWorldToLocal(vec3 v) {
  vec3 q = v;
  if (!u_dbgBypassSierpinskiAlign) q = sierpAlignFwd() * q;
  q = rotate3D(q, u_rotation);
  return q;
}

float orbitTrapBoxy(vec3 fp);

float deMengerWorld(vec3 p) {
  vec3 fp = mgToLocal(p);
  return sdMenger(fp, u_iterations) * u_fractalScale;
}

float deMandelbulbWorld(vec3 p) {
  vec3 fp = mbToLocal(p);
  return sdMandelbulb(fp, u_iterations, u_fractalPower) * u_fractalScale;
}

vec2 deMandelbulbWorldWithTrap(vec3 p) {
  vec3 fp = mbToLocal(p);
  vec2 r = sdMandelbulbWithTrap(fp, u_iterations, u_fractalPower);
  return vec2(r.x * u_fractalScale, r.y);
}

float deSierpinskiWorld(vec3 p) {
  vec3 fp = spToLocal(p);
  return sdSierpinskiWithBase(fp, u_iterations, 2.0, max(0.2, u_sierpinskiBase)) * u_fractalScale;
}

float deMandelboxWorld(vec3 p) {
  vec3 fp = mbxToLocal(p);
  return sdMandelboxSimple(fp, u_iterations, -1.5) * (u_fractalScale * MB_NORM);
}

float deAmazingSurfWorld(vec3 p, out float trap) {
  vec3 fp = rotate3D(p, u_rotation);
  if (u_worldUseDEC) {
    
    float s = max(0.2, u_fractalScale);
    vec3 rotated = rotate3D(p - u_decOffset, u_rotation);
    vec3 pl = (rotated - u_zoomCenter) / s;
    float d0 = decUserDE(pl) * s;
    float d;
    if (u_worldThickness <= 0.001) {
      
      d = d0;
    } else {
      
      d = abs(d0) - clamp(u_worldThickness, 0.0, 0.6);
    }
    
    float trapBox = orbitTrapBoxy(pl);
    float trapRad = length(pl) * 0.5;
    trap = mix(trapBox, trapRad, 0.35);
    return d * clamp(u_worldDeScale, 0.5, 1.0);
  } else {
    float d = deAmazingSurf(fp,
                            max(2.0, u_worldTile),
                            clamp(u_worldThickness, 0.02, 0.8),
                            clamp(u_worldWarp, 0.0, 2.0),
                            max(0.2, u_fractalScale),
                            trap) * clamp(u_worldDeScale, 0.5, 1.0);
    return d;
  }
}

float orbitTrapBoxy(vec3 fp) {
  float t1 = abs(fp.x);
  float t2 = abs(fp.y);
  float t3 = abs(fp.z);
  float t4 = length(fp.xy);
  float t5 = length(fp.yz);
  return (t1 + t2 + t3 + 0.5 * (t4 + t5)) / 4.0;
}

float boundsDistanceWorld(vec3 p) {
  if (u_fractalType == 0) {
    return length(p) - 4.0;
  }
  if (u_fractalType == 1) {
    vec3 q = mgToLocal(p);
    return sdBox(q, vec3(1.1)) * u_fractalScale;
  }
  if (u_fractalType == 2) {
    vec3 q = mbToLocal(p);
    return (length(q) - 2.2) * u_fractalScale;
  }
  if (u_fractalType == 4) {
    vec3 q = mbxToLocal(p);
    const float MB_PAD = 0.6;
    return (sdBox(q, vec3(2.2)) - MB_PAD) * (u_fractalScale * MB_NORM);
  }
  if (u_fractalType == 5) {
    
    vec3 rotated = rotate3D(p, u_rotation);
    vec3 q = (rotated - u_zoomCenter) / max(0.5, u_fractalScale);
    float rad = max(2.0, u_worldTile * 0.9);
    return (length(q) - rad) * max(0.5, u_fractalScale);
  }
  if (u_fractalType == 6) {
    
    vec3 rotated = rotate3D(p, u_rotation);
    vec3 q = (rotated - u_zoomCenter) / max(0.5, u_worldTile);
    
    
    return (length(q) - 1.05) * max(0.5, u_worldTile);
  }
  
  vec3 rotated = rotate3D(p, u_rotation);
  vec3 q = (rotated - u_zoomCenter) / u_fractalScale;
  return (length(q) - 2.6) * u_fractalScale;
}

vec3 texSamplePos(vec3 p) {
  if (u_texSpaceMode == 0) {
    return p; 
  }
  if (u_fractalType == 1) return mgToLocal(p);
  if (u_fractalType == 2) return mbToLocal(p);
  if (u_fractalType == 3) return spToLocal(p);
  if (u_fractalType == 4) return mbxToLocal(p);
  if (u_fractalType == 5) {
    vec3 rotated = rotate3D(p, u_rotation);
    return (rotated - u_zoomCenter) / max(0.2, u_fractalScale);
  }
  if (u_fractalType == 6) {
    vec3 rotated = rotate3D(p, u_rotation);
    return (rotated - u_zoomCenter) / max(0.5, u_worldTile);
  }
  if (u_fractalType == 7) {
    vec3 rotated = rotate3D(p - u_decOffset, u_rotation);
    return (rotated - u_zoomCenter) / max(0.2, u_fractalScale);
  }
  vec3 rotated = rotate3D(p, u_rotation);
  return (rotated - u_zoomCenter) / max(0.2, u_fractalScale);
}

void evalProceduralTextures(vec3 pt, vec3 n, out float colorFactor, out vec3 bumpDelta, out float specScaleOut) {
  colorFactor = 1.0;
  bumpDelta = vec3(0.0);
  specScaleOut = 1.0;

  
  if (u_worldDetailStrength > 0.0) {
    float dcol = triplanarFbmDetail(pt, n, max(0.05, u_worldDetailScale));
    float scol = clamp(u_worldDetailStrength, 0.0, 1.0);
    colorFactor *= (1.0 + (dcol - 0.5) * 0.6 * scol);
  }

  
  bool hasA = (u_worldTexType > 0);
  bool hasB = (u_worldTexTypeB > 0);
  
  float colA = clamp(u_worldTexColorStrength, 0.0, 1.0);
  float colB = clamp(u_worldTexColorStrengthB, 0.0, 1.0);
  float bA   = clamp(u_worldTexBumpStrength, 0.0, 1.0);
  float bB   = clamp(u_worldTexBumpStrengthB, 0.0, 1.0);
  float sPA  = clamp(u_worldTexSpecStrength, 0.0, 1.0);
  float sPB  = clamp(u_worldTexSpecStrengthB, 0.0, 1.0);

  if (!(hasA || hasB)) return; 
  if ((colA + colB + bA + bB + sPA + sPB) <= 0.0) return; 

  float sA = max(0.05, u_worldTexScale);
  float sB = max(0.05, u_worldTexScaleB);
  float texA = hasA ? worldTextureValue(pt, n, sA, u_worldTexType) : 0.0;
  float texB = hasB ? worldTextureValue(pt, n, sB, u_worldTexTypeB) : 0.0;
  if (hasA || hasB) {
    float alphaC = clamp(u_worldTexBlendAlphaColor, 0.0, 1.0);
    float alphaN = clamp(u_worldTexBlendAlphaBump, 0.0, 1.0);
    float alphaS = clamp(u_worldTexBlendAlphaSpec, 0.0, 1.0);

    
    float texMixC, texMixS;
    float wAc=0.0, wBc=0.0, wAn=0.0, wBn=0.0, wAs=0.0, wBs=0.0;
    if (u_worldTexBlendMode == 1) {
      
      texMixC = texA * (hasB ? texB : 1.0);
      texMixS = texMixC;
      wAc = (hasB ? texB : 1.0); wBc = texA;
      wAn = wAc; wBn = wBc;
      wAs = wAc; wBs = wBc;
    } else if (u_worldTexBlendMode == 2) {
      
      texMixC = clamp(texA + (hasB ? texB : 0.0), 0.0, 1.0);
      texMixS = texMixC;
      wAc = 1.0; wBc = 1.0;
      wAn = 1.0; wBn = 1.0;
      wAs = 1.0; wBs = 1.0;
    } else {
      
      texMixC = mix(texA, (hasB ? texB : texA), alphaC);
      texMixS = mix(texA, (hasB ? texB : texA), alphaS);
      wAc = 1.0 - (hasB ? alphaC : 0.0);
      wBc = (hasB ? alphaC : 0.0);
      wAn = 1.0 - (hasB ? alphaN : 0.0);
      wBn = (hasB ? alphaN : 0.0);
      wAs = 1.0 - (hasB ? alphaS : 0.0);
      wBs = (hasB ? alphaS : 0.0);
    }

    
    float colW = (u_worldTexBlendMode == 0) ? mix(colA, colB, (hasB ? alphaC : 0.0)) : clamp(colA + (hasB ? colB : 0.0), 0.0, 1.0);
    colorFactor *= (1.0 + (texMixC - 0.5) * 1.0 * colW);

    
    float bump = (u_worldTexBlendMode == 0) ? mix(bA, bB, (hasB ? alphaN : 0.0)) : clamp(bA + (hasB ? bB : 0.0), 0.0, 1.0);
    
    float sMax = max(sA, sB);
    vec2 ux_ = pt.yz * sMax; vec2 uy_ = pt.zx * sMax; vec2 uz_ = pt.xy * sMax;
    float fw_ = max(max(length(fwidth(ux_)), length(fwidth(uy_))), length(fwidth(uz_)));
    float lodGrad_ = (u_texLODEnabled ? smoothstep(0.6, 2.0, fw_) : 0.0);
    float bumpL = mix(1.0, clamp(u_texLODBumpFactor, 0.0, 1.0), lodGrad_);
    bump *= bumpL;
    if (u_texBumpDerivFade > 0.0) bump *= (1.0 - clamp(u_texBumpDerivFade, 0.0, 1.0) * lodGrad_);
    
    if (u_texRoughFadeK > 0.0) {
      float rough = length(fwidth(n));
      float rf = clamp(rough * u_texRoughFadeK, 0.0, 1.0);
      bump *= (1.0 - rf);
    }
    
    if (u_worldTexAutoAtten) {
      float bigS = max(u_worldTexScale, u_worldTexScaleB);
      float att = 1.0 - smoothstep(18.0, 36.0, bigS) * clamp(u_worldTexAAStrength, 0.0, 1.0);
      bump *= att;
    }
    
    float distFade = 1.0;
    if (u_texFadeFar > u_texFadeNear) {
      float d = length(pt - u_cameraPos);
      distFade = 1.0 - smoothstep(u_texFadeNear, u_texFadeFar, d);
    }
    bump *= distFade;

    
    if (bump > 0.001) {
      float epsB = 0.01;
      vec3 gx = vec3(epsB,0,0), gy = vec3(0,epsB,0), gz = vec3(0,0,epsB);
      vec3 gradT = vec3(0.0);
      if (u_texFastBump) {
        float effA = (hasA ? (wAn * bA) : 0.0);
        float effB = (hasB ? (wBn * bB) : 0.0);
        bool useA = (effA >= effB);
        float t0 = useA ? texA : texB;
        int   tType = useA ? u_worldTexType : u_worldTexTypeB;
        float sc   = useA ? sA : sB;
        vec3 g = vec3(
          worldTextureValue(pt + gx, n, sc, tType) - t0,
          worldTextureValue(pt + gy, n, sc, tType) - t0,
          worldTextureValue(pt + gz, n, sc, tType) - t0
        ) / epsB;
        gradT = useA ? (g * wAn) : (g * wBn);
      } else {
        vec3 gradA = vec3(0.0), gradB = vec3(0.0);
        if (hasA && (wAn * bA) > 1e-5) {
          float t0 = texA;
          gradA = vec3(
            worldTextureValue(pt + gx, n, sA, u_worldTexType) - t0,
            worldTextureValue(pt + gy, n, sA, u_worldTexType) - t0,
            worldTextureValue(pt + gz, n, sA, u_worldTexType) - t0
          ) / epsB;
        }
        if (hasB && (wBn * bB) > 1e-5) {
          float t1 = texB;
          gradB = vec3(
            worldTextureValue(pt + gx, n, sB, u_worldTexTypeB) - t1,
            worldTextureValue(pt + gy, n, sB, u_worldTexTypeB) - t1,
            worldTextureValue(pt + gz, n, sB, u_worldTexTypeB) - t1
          ) / epsB;
        }
        gradT = gradA * wAn + gradB * wBn;
      }
      gradT -= n * dot(gradT, n);
      bumpDelta = gradT * (0.4 * bump);
    }

    
    float sp = (u_worldTexBlendMode == 0) ? mix(sPA, sPB, (hasB ? alphaS : 0.0)) : clamp(sPA + (hasB ? sPB : 0.0), 0.0, 1.0);
    if (u_texLODEnabled) {
      float sMax2 = max(sA, sB);
      vec2 ux2 = pt.yz * sMax2; vec2 uy2 = pt.zx * sMax2; vec2 uz2 = pt.xy * sMax2;
      float fw2 = max(max(length(fwidth(ux2)), length(fwidth(uy2))), length(fwidth(uz2)));
      float lodGrad2 = smoothstep(0.6, 2.0, fw2);
      float specL = mix(1.0, clamp(u_texLODSpecFactor, 0.0, 1.0), lodGrad2);
      sp *= specL;
      if (u_texSpecDerivFade > 0.0) sp *= (1.0 - clamp(u_texSpecDerivFade, 0.0, 1.0) * lodGrad2);
    }
    if (u_worldTexAutoAtten) {
      float bigS2 = max(u_worldTexScale, u_worldTexScaleB);
      float att2 = 1.0 - smoothstep(18.0, 36.0, bigS2) * clamp(u_worldTexAAStrength, 0.0, 1.0);
      sp *= att2;
    }
    
    sp *= distFade;
    specScaleOut = 1.0 + (texMixS - 0.5) * 0.6 * sp;
  }
}

vec2 sdMandelbulbWithTrap(vec3 p, int iterations, float power) {
  vec3 z = p;
  float dr = 1.0;
  float r = 0.0;
  float orbitTrap = 1000.0; 
  int iterCount = 0; 

  for(int i = 0; i < 20; i++) {
    if(i >= iterations) break;

    r = length(z);

    
    
    float trap1 = abs(z.x);              
    float trap2 = abs(z.y);              
    float trap3 = abs(z.z);              
    float trap4 = length(z.xy);          
    float trap5 = abs(z.x - z.y);        

    
    float minTrap = min(min(min(trap1, trap2), min(trap3, trap4)), trap5);

    
    orbitTrap = min(orbitTrap, minTrap);
    iterCount++;

    if(r > 2.0) break;

    float theta = acos(z.z / r);
    float phi = atan(z.y, z.x);

    dr = pow(r, power - 1.0) * power * dr + 1.0;

    float zr = pow(r, power);
    theta = theta * power;
    phi = phi * power;

    z = zr * vec3(
      sin(theta) * cos(phi),
      sin(phi) * sin(theta),
      cos(theta)
    );
    z += p;
  }

  
  if (orbitTrap > 100.0 || iterCount < 2) {
    
    orbitTrap = min(min(abs(p.x), abs(p.y)), abs(p.z));
  }

  r = max(r, 1e-6); 
  return vec2(0.5 * log(r) * r / dr, orbitTrap);
}

float mapRaw(vec3 p) {
  float fractal = MAX_DIST;
  switch (u_fractalType) {
    case FT_PRIMITIVES: {
      vec3 spherePos = vec3(sin(u_time * 0.75) * 2.0, 0.0, 0.0);
      float sphere = sdSphere(p - spherePos, 1.0);
      vec3 boxPos = vec3(0.0, 0.0, 0.0);
      float box = sdBox(rotate3D(p - boxPos, u_rotation), vec3(0.8, 0.8, 0.8));
      fractal = opSubtraction(box, sphere);
    } break;
    case FT_MENGER: { fractal = deMengerWorld(p); } break;
    case FT_BULB: { fractal = deMandelbulbWorld(p); } break;
    case FT_SIERPINSKI: { fractal = deSierpinskiWorld(p); } break;
    case FT_MANDELBOX: { fractal = deMandelboxWorld(p); } break;
    case FT_WORLD: { float trapDummy; fractal = deAmazingSurfWorld(p, trapDummy); } break;
    case FT_TRUCHET: { float trapDummy; fractal = deTruchetPipesWorld(p, trapDummy); } break;
    case FT_DEC: {
      float s = max(0.2, u_fractalScale);
      vec3 rotated = rotate3D(p - u_decOffset, u_rotation);
      vec3 pl = (rotated - u_zoomCenter) / s;
      float d = decUserDE(pl) * s;
      fractal = d;
    } break;
  }

  
  
  

  
  
  
  
  return fractal;
}

float mapShadow(vec3 p) {
  if (u_fractalType == FT_TRUCHET && u_fastShadows) {
    float trapDummy;
    return deTruchetPipesWorldShadow(p, trapDummy);
  }
  return mapRaw(p);
}

float mapAO(vec3 p) {
  if (u_fractalType == FT_TRUCHET && u_fastAO) {
    return mapShadow(p);
  }
  return mapRaw(p);
}

float mapCulled(vec3 p) {
  if (u_enableBoundsCulling) {
    if (u_fractalType == FT_DEC || (u_fractalType == FT_WORLD && u_worldUseDEC)) {
      
      return mapRaw(p);
    }
    float bounds = (u_fractalType == 0) ? (length(p) - 4.0) : boundsDistanceWorld(p);
    if (bounds > u_boundsCullMargin) {
      return bounds;
    }
  }
  return mapRaw(p);
}

float map(vec3 p) { return mapCulled(p); }

vec2 mapWithTrapRaw(vec3 p) {
  float fractal = MAX_DIST;
  float orbitTrap = 0.0;

  switch (u_fractalType) {
    case FT_PRIMITIVES: {
      vec3 spherePos = vec3(sin(u_time * 0.75) * 2.0, 0.0, 0.0);
      float sphere = sdSphere(p - spherePos, 1.0);
      vec3 boxPos = vec3(0.0, 0.0, 0.0);
      float box = sdBox(rotate3D(p - boxPos, u_rotation), vec3(0.8, 0.8, 0.8));
      fractal = opSubtraction(box, sphere);
      orbitTrap = length(p);
    } break;
    case FT_MENGER: {
      fractal = deMengerWorld(p);
      vec3 fp = mgToLocal(p);
      orbitTrap = orbitTrapBoxy(fp);
    } break;
    case FT_BULB: {
      vec2 result = deMandelbulbWorldWithTrap(p);
      fractal = result.x; orbitTrap = result.y;
    } break;
    case FT_SIERPINSKI: {
      fractal = deSierpinskiWorld(p);
      vec3 fp = spToLocal(p);
      orbitTrap = orbitTrapBoxy(fp);
    } break;
    case FT_MANDELBOX: {
      fractal = deMandelboxWorld(p);
      vec3 fpl = mbxToLocal(p);
      orbitTrap = orbitTrapBoxy(fpl);
    } break;
    case FT_WORLD: {
      float trap; float d = deAmazingSurfWorld(p, trap);
      fractal = d; orbitTrap = trap;
    } break;
    case FT_TRUCHET: {
      float trapT; float d = deTruchetPipesWorld(p, trapT);
      fractal = d; orbitTrap = trapT;
    } break;
    case FT_DEC: {
      float s = max(0.2, u_fractalScale);
      vec3 rotated = rotate3D(p - u_decOffset, u_rotation);
      vec3 pl = (rotated - u_zoomCenter) / s;
      float d = decUserDE(pl) * s;
      
      float trapBox = orbitTrapBoxy(pl);
      float trapRad = length(pl) * 0.5;
      orbitTrap = mix(trapBox, trapRad, 0.35);
      fractal = d;
    } break;
  }

  
  return vec2(fractal, orbitTrap);
}

vec2 mapWithTrapCulled(vec3 p) {
  if (u_enableBoundsCulling) {
    if (u_fractalType == 3 || u_fractalType == 4 || u_fractalType == 5 || u_fractalType == 6 || u_fractalType == FT_DEC) {
      return mapWithTrapRaw(p);
    }
    float bounds;
    if (u_fractalType == 0) {
      bounds = length(p) - 4.0;
    } else {
      bounds = boundsDistanceWorld(p);
    }
    if (bounds > u_boundsCullMargin) {
      return vec2(bounds, 0.0);
    }
  }
  return mapWithTrapRaw(p);
}

vec2 mapWithTrap(vec3 p) { return mapWithTrapCulled(p); }

vec3 calcNormalCore(vec3 p, float epsilon, bool fractalOnly) {
  const vec2 k = vec2(1.0, -1.0);
  vec3 s1 = p + k.xyy * epsilon;
  vec3 s2 = p + k.yyx * epsilon;
  vec3 s3 = p + k.yxy * epsilon;
  vec3 s4 = p + k.xxx * epsilon;
  float m1 = fractalOnly ? mapFractalOnly(s1) : mapRaw(s1);
  float m2 = fractalOnly ? mapFractalOnly(s2) : mapRaw(s2);
  float m3 = fractalOnly ? mapFractalOnly(s3) : mapRaw(s3);
  float m4 = fractalOnly ? mapFractalOnly(s4) : mapRaw(s4);
  vec3 n = k.xyy * m1 + k.yyx * m2 + k.yxy * m3 + k.xxx * m4;
  return normalize(n);
}

vec3 calcNormalEnhanced(vec3 p, float epsilon) {
  return calcNormalCore(p, epsilon, false);
}

vec3 calcNormalEnhanced(vec3 p) {
  return calcNormalEnhanced(p, u_normalEpsilon);
}

vec3 calcNormalEnhancedFractal(vec3 p, float epsilon) {
  return calcNormalCore(p, epsilon, true);
}

vec3 calcNormalFast(vec3 p, float epsilon) {
  float f0 = mapRaw(p);
  float dx = mapRaw(p + vec3(epsilon, 0.0, 0.0)) - f0;
  float dy = mapRaw(p + vec3(0.0, epsilon, 0.0)) - f0;
  float dz = mapRaw(p + vec3(0.0, 0.0, epsilon)) - f0;
  return normalize(vec3(dx, dy, dz));
}

float calcAOAdvanced(vec3 p, vec3 normal) {
  
  float viewDistance = length(p - u_cameraPos);
  int samples = max(1, u_aoMaxSamples); 
  if (u_enableBudgetLOD) {
    float lodFactor = smoothstep(u_lodNear, u_lodFar, viewDistance);
    float s = mix(5.0, float(u_aoMinSamples), lodFactor);
    samples = max(1, int(floor(s + 0.5)));
  }
  
  
  if (u_fractalType == FT_TRUCHET) {
    float k = 0.85 - 0.25 * smoothstep(u_lodNear * 0.6, u_lodFar, viewDistance);
    samples = max(1, int(floor(float(samples) * k + 0.5)));
  }

  float occ = 0.0;
  float sca = 1.0;
  const int MAX_AO_SAMPLES = 8;
  float occExit = (u_fractalType == FT_TRUCHET && u_fastAO) ? 0.55 : 0.65;
  for(int i = 0; i < MAX_AO_SAMPLES; i++) {
    if (i >= samples) break;
    float t = float(i) / max(1.0, float(samples - 1));
    float spread = u_enableBudgetLOD ? mix(1.0, 1.4, smoothstep(u_lodNear, u_lodFar, viewDistance)) : 1.0;
    float h = (0.02 + 0.15 * t) * spread;
    float d = mapAO(p + h * normal);
    
    if (isnan(d) || isinf(d)) d = h; 
    
    
    d = clamp(d, -h, h * 4.0);
    occ += (h - d) * sca;
    sca *= 0.95;
    
    if (occ > occExit) break;
  }
  float ao = clamp(1.0 - 1.5 * occ, 0.0, 1.0);
  return ao;
}

float calcSoftShadowBudgeted(vec3 ro, vec3 rd, vec3 n0, float mint, float maxt) {
  
  if (u_enableBudgetLOD && maxt > (u_lodFar * u_farShadowSkipFactor)) {
    return 1.0;
  }

  
  int stepsBudget = u_softShadowSteps;
  if (u_enableBudgetLOD) {
    float lodFactor = smoothstep(u_lodNear, u_lodFar, maxt);
    float dyn = mix(float(u_softShadowSteps), max(float(u_softShadowMinSteps), float(u_softShadowSteps) * 0.4), lodFactor);
    stepsBudget = int(floor(dyn));
  }
  
  if (u_fractalType == FT_TRUCHET) {
    float nDotL = max(0.0, dot(n0, rd));
    float k = mix(0.45, 1.0, smoothstep(0.10, 0.60, nDotL));
    stepsBudget = max(int(floor(float(stepsBudget) * k + 0.5)), u_softShadowMinSteps);
  }

  float res = 1.0;
  float t = mint;
  
  
  
  float graze = 1.0 - max(0.0, dot(n0, rd));
  float angleAtten = smoothstep(0.2, 0.85, graze); 
  if (u_enableDithering) {
    float j = u_useBlueNoise ? blueNoiseHash(gl_FragCoord.xy) : interleavedGradientNoise(gl_FragCoord.xy);
    t += (j - 0.5) * 0.01 * clamp(u_ditheringStrength * angleAtten, 0.0, 1.0);
  }
  if (u_shadowDitherStrength > 0.0) {
    float j2 = fract(sin(dot(gl_FragCoord.xy, vec2(127.1,311.7))) * 43758.5453);
    t += (j2 - 0.5) * 0.02 * clamp(u_shadowDitherStrength * angleAtten, 0.0, 1.0);
  }
  const int MAX_SOFT_STEPS = 64;
  for(int i = 0; i < MAX_SOFT_STEPS; i++) {
    if(i >= stepsBudget) break;
    vec3 sp = ro + rd * t;
    float h = mapShadow(sp);
    
    
    
    if (isnan(h) || isinf(h)) {
      h = 0.02;
    }
    
    float angleBias = u_shadowBiasAngle * (1.0 - max(0.0, dot(n0, rd)));
    
    if (u_fractalType == FT_TRUCHET) {
      angleBias += 0.0003;
    }
    float bias = u_shadowBiasBase + u_shadowBiasSlope * t + angleBias;
    h = max(h, bias);

    
    if (u_floorEnabled) {
      float dGround = sdPlaneXZ(sp + vec3(0.0, 2.0, 0.0));
      if (abs(dGround) < u_shadowPlaneBias * 4.0) {
        h = max(h, u_shadowPlaneBias + 0.5 * u_shadowBiasSlope * t);
      }
    }
    
    
    float c = u_shadowSharpness * h / max(t, 0.001);
    c = 1.0 - exp(-max(0.0, c));
    res = min(res, c);
    
    if (u_floorEnabled) {
      float dG2 = sdPlaneXZ(sp + vec3(0.0, 2.0, 0.0));
      float ease2 = 1.0 - smoothstep(u_shadowPlaneBias * 0.5, u_shadowPlaneBias * 2.5, abs(dG2));
      
      res = max(res, mix(0.0, 0.35, ease2));
    }
    float stepClamp = (u_shadowStepClamp > 0.0 ? u_shadowStepClamp : (u_enableBudgetLOD ? 0.2 : 0.15));
    
    
    float minStep = 0.005 + 0.01 * smoothstep(0.0, maxt, t);
    t += clamp(h, minStep, stepClamp);
    if (u_shadowEarlyExit > 0.0 && res < u_shadowEarlyExit) break;
    if(h < 0.001 || t > maxt) break;
  }
  return clamp(res, 0.0, 1.0);
}

vec3 getRayDirectionRobust(vec2 uv, vec3 ro, vec3 target, float fov) {
  vec3 forward = normalize(target - ro);
  
  vec3 right = normalize(u_camRight);
  
  right = normalize(right - forward * dot(right, forward));
  vec3 up = normalize(cross(right, forward));

  float fovScale = tan(fov * 0.5 * PI / 180.0);
  vec3 rd = normalize(forward + fovScale * (uv.x * right + uv.y * up));
  return rd;
}

float getAdaptiveEpsilon(float distance) {
  
  
  float base = MIN_DIST;
  if (u_fractalType == 3) {
    base *= 0.25; 
    base /= max(u_fractalScale, 1.0); 
  }
  if (!u_enableDistanceLOD) {
    return base;
  }
  float lodFactor = smoothstep(u_lodNear, u_lodFar, distance);
  return base * (1.0 + lodFactor * 1.0);
}

int getAdaptiveMaxSteps(float distance) {
  int baseSteps = u_maxSteps;
  if (!u_enableBudgetLOD) return baseSteps;
  
  float lodFactor = smoothstep(u_lodNear, u_lodFar, distance);
  float farSteps = max(16.0, float(baseSteps) * clamp(u_budgetStepsFarFactor, 0.1, 1.0));
  float dynSteps = mix(float(baseSteps), farSteps, lodFactor);
  return int(floor(dynSteps));
}

float getAdaptiveRelaxation(float distance, vec3 normal) {
  if (!u_adaptiveRelaxation) {
    return u_stepRelaxation; 
  }

  
  float distanceFactor = smoothstep(u_lodNear, u_lodFar, distance);
  return mix(u_relaxationMin, u_relaxationMax, distanceFactor);
}

vec3 rayMarch(vec3 ro, vec3 rd) {
  float t = 0.0;
  float prevT = 0.0;
  float curvFactor = 1.0; 
  float prevD = -1.0;

  for (int i = 0; i < 300; i++) {
    int maxStepsDynamic = getAdaptiveMaxSteps(t);
    if (i >= maxStepsDynamic) break;

    vec3 p = ro + rd * t;
    float d = mapCulled(p);

    
    float adaptiveEpsilon = getAdaptiveEpsilon(t);
    float ditherOffset = ditherNearSurface(d, adaptiveEpsilon);

    
    float hitThreshold = adaptiveEpsilon * (u_conservativeHits ? 0.75 : 1.0) + ditherOffset * (u_conservativeHits ? 0.5 : 1.0);
    if (d < hitThreshold) {
      
      float b = t;
      float a = prevT;
      float tHit = b;
      {
        int refine = u_conservativeHits ? 12 : 8;
        if (u_fractalType == FT_WORLD || u_fractalType == FT_TRUCHET) refine += 2;
        for (int r = 0; r < 16; r++) {
          if (r >= refine) break;
          float m = 0.5 * (a + b);
          float dm = mapFractalOnly(ro + rd * m);
          if (dm > adaptiveEpsilon) a = m; else b = m;
        }
        tHit = b;
      }
      return vec3(tHit, float(i), 1.0); 
    }

    if (t > MAX_DIST) {
      break;
    }

    
    float omega = u_stepRelaxation;

    
    if (u_curvatureAwareRelaxation) {
      bool applyCurv = true;
      if (u_curvatureNearOnly) {
        float epsN = getAdaptiveEpsilon(t);
        applyCurv = (d < epsN * max(1.0, u_curvatureNearK));
      }
      if (applyCurv && prevD >= 0.0) {
        float denom = max(max(d, prevD), 0.0005);
        float kRel = abs(d - prevD) / denom; 
        float curvSample = clamp(kRel * 6.0, 0.0, 1.0); 
        float target = 1.0 - curvSample; 
        curvFactor = mix(curvFactor, target, 0.25); 
      }
    }
    if (u_adaptiveRelaxation && u_curvatureAwareRelaxation) {
      
      float distanceFactor = smoothstep(u_lodNear, u_lodFar, t);
      float factor = curvFactor * 0.7 + distanceFactor * 0.3;
      float dyn = mix(u_relaxationMin, u_relaxationMax, factor);
      omega = u_stepRelaxation * dyn;
    } else if (u_adaptiveRelaxation) {
      
      float distanceFactor = smoothstep(u_lodNear, u_lodFar, t);
      float dyn = mix(u_relaxationMin, u_relaxationMax, distanceFactor);
      omega = u_stepRelaxation * dyn;
    } else if (u_curvatureAwareRelaxation) {
    
      float dyn = mix(u_relaxationMin, u_relaxationMax, curvFactor);
      omega = u_stepRelaxation * dyn;
    }
  
  {
    float adaptiveEpsilon = getAdaptiveEpsilon(t);
    
    float near = smoothstep(adaptiveEpsilon * 2.0, adaptiveEpsilon * 12.0, d);
    float omegaCap = (u_fractalType == FT_TRUCHET ? 0.94 : 0.92);
    float omegaSafe = min(omega, omegaCap);
    omega = mix(omegaSafe, omega, near);
  }

    
    float stepLen = d * omega;
    
    if (u_integratorAuto && t > u_integratorSwitchDist) {
      float remaining = MAX_DIST - t;
      float frac = clamp(u_segmentFraction, 0.1, 0.9);
      stepLen = min(stepLen, remaining * frac);
    }
    float safety = clamp(u_stepSafety, 0.5, 1.0);
    if (u_stepSafetyAuto) {
      float safetyCurv = mix(u_stepSafetyMin, u_stepSafetyMax, curvFactor);
      float w = 1.0 - smoothstep(u_stepSafetyBandNear, u_stepSafetyBandFar, t);
      float safetyDist = mix(u_stepSafetyMax, u_stepSafetyMin, w);
      safety = min(safety, min(safetyCurv, safetyDist));
    }
    prevT = t;
    prevD = d;
    t += stepLen * safety;
  }

  return vec3(t, 0.0, 0.0); 
}

vec4 rayMarchWithTrap(vec3 ro, vec3 rd) {
  float t = 0.0;
  float prevT = 0.0;
  float orbitTrap = 0.0;
  float curvFactor = 1.0;
  float prevD = -1.0;

  for (int i = 0; i < 300; i++) {
    int maxStepsDynamic = getAdaptiveMaxSteps(t);
    if (i >= maxStepsDynamic) break;

    vec3 p = ro + rd * t;
    vec2 result = mapWithTrapCulled(p);
    float d = result.x;

    float adaptiveEpsilon = getAdaptiveEpsilon(t);
    float ditherOffset = ditherNearSurface(d, adaptiveEpsilon);

    

    
    float hitThresholdS = adaptiveEpsilon * (u_conservativeHits ? 0.75 : 1.0) + ditherOffset * (u_conservativeHits ? 0.5 : 1.0);
    if (d < hitThresholdS) {
      
      orbitTrap = result.y;
      float a = prevT;
      float b = t;
      int refineS = u_conservativeHits ? 12 : 8;
      if (u_fractalType == FT_WORLD || u_fractalType == FT_TRUCHET) refineS += 2;
      for (int r = 0; r < 16; r++) {
        if (r >= refineS) break;
        float m = 0.5 * (a + b);
        float dm = mapFractalOnly(ro + rd * m);
        if (dm > adaptiveEpsilon) a = m; else b = m;
      }
      return vec4(b, float(i), 1.0, orbitTrap); 
    }

    if (t > MAX_DIST) {
      break;
    }

    
    float omega = u_stepRelaxation;
    bool autoPast = (u_integratorAuto && t > u_integratorSwitchDist);

    if (u_curvatureAwareRelaxation && !autoPast) {
      bool applyCurv = true;
      if (u_curvatureNearOnly) {
        float epsN = getAdaptiveEpsilon(t);
        applyCurv = (d < epsN * max(1.0, u_curvatureNearK));
      }
      if (applyCurv && prevD >= 0.0) {
        float denom = max(max(d, prevD), 0.0005);
        float kRel = abs(d - prevD) / denom;
        float curvSample = clamp(kRel * 6.0, 0.0, 1.0);
        float target = 1.0 - curvSample;
        curvFactor = mix(curvFactor, target, 0.25);
      }
    }

    if (!autoPast && u_adaptiveRelaxation && u_curvatureAwareRelaxation) {
      float distanceFactor = smoothstep(u_lodNear, u_lodFar, t);
      float factor = curvFactor * 0.7 + distanceFactor * 0.3;
      float dyn = mix(u_relaxationMin, u_relaxationMax, factor);
      omega = u_stepRelaxation * dyn;
    } else if (!autoPast && u_adaptiveRelaxation) {
      float distanceFactor = smoothstep(u_lodNear, u_lodFar, t);
      float dyn = mix(u_relaxationMin, u_relaxationMax, distanceFactor);
      omega = u_stepRelaxation * dyn;
    } else if (!autoPast && u_curvatureAwareRelaxation) {
      float dyn = mix(u_relaxationMin, u_relaxationMax, curvFactor);
      omega = u_stepRelaxation * dyn;
    }
    
    {
      float adaptiveEpsilon = getAdaptiveEpsilon(t);
      float near = smoothstep(adaptiveEpsilon * 2.0, adaptiveEpsilon * 12.0, d);
      float omegaSafe = min(omega, 0.92);
      omega = mix(omegaSafe, omega, near);
    }

    
    float stepLenS = d * omega;
    if (autoPast) {
      float remainingS = MAX_DIST - t;
      float fracS = clamp(u_segmentFraction, 0.1, 0.9);
      stepLenS = remainingS * fracS;
    }
    float safetyS = clamp(u_stepSafety, 0.5, 1.0);
    if (u_stepSafetyAuto) {
      float safetyCurvS = mix(u_stepSafetyMin, u_stepSafetyMax, curvFactor);
      float wS = 1.0 - smoothstep(u_stepSafetyBandNear, u_stepSafetyBandFar, t);
      float safetyDistS = mix(u_stepSafetyMax, u_stepSafetyMin, wS);
      safetyS = min(safetyS, min(safetyCurvS, safetyDistS));
    }
    prevT = t;
    prevD = d;
    t += stepLenS * safetyS;
  }

  return vec4(t, 0.0, 0.0, orbitTrap); 
}

vec4 rayMarchWithTrap_Segment(vec3 ro, vec3 rd) {
  float t = 0.0;
  float orbitTrap = 0.0;
  const float maxDist = MAX_DIST;

  for (int i = 0; i < 300; i++) {
    int maxStepsDynamic = getAdaptiveMaxSteps(t);
    if (i >= maxStepsDynamic) break;

    vec3 p = ro + rd * t;
    vec2 result = mapWithTrap(p);
    float d = result.x;

    float adaptiveEpsilon = getAdaptiveEpsilon(t);
    float ditherOffset = ditherNearSurface(d, adaptiveEpsilon);

    if (d < adaptiveEpsilon + ditherOffset) {
      orbitTrap = result.y;
      return vec4(t, float(i), 1.0, orbitTrap);
    }

    if (t > maxDist) break;

    float remaining = maxDist - t;
    float frac = clamp(u_segmentFraction, 0.1, 0.9);
    
    
    if (u_fractalType == 5 || u_fractalType == 6) {
      frac = min(frac, 0.45);
    }
    float stepSeg = min(d, remaining * frac);
    if (u_fractalType == 5 || u_fractalType == 6) {
      
      float clampK = clamp(u_worldSegClamp, 0.5, 1.0);
      
      float thin;
      if (u_fractalType == 5) {
        thin = clamp((0.16 - u_worldThickness) * 6.0, 0.0, 1.0); 
      } else {
        thin = clamp((0.12 - u_truchetRadius) * 8.0, 0.0, 1.0);
      }
      clampK = mix(clampK, min(clampK, 0.70), thin);
      float adaptiveEpsilon = getAdaptiveEpsilon(t);
      float nearFactor = (d < adaptiveEpsilon * 12.0) ? 0.6 : 1.0;
      stepSeg = min(stepSeg, d * clampK * nearFactor);
    }
    float safetySeg = clamp(u_stepSafety, 0.5, 1.0);
    if (u_stepSafetyAuto) {
      
      float w = 1.0 - smoothstep(u_stepSafetyBandNear, u_stepSafetyBandFar, t);
      float safetyDist = mix(u_stepSafetyMax, u_stepSafetyMin, w);
      safetySeg = min(safetySeg, safetyDist);
    }
    t += stepSeg * safetySeg;
  }

  return vec4(t, 0.0, 0.0, orbitTrap);
}

vec3 getMaterialColor(vec3 p) {
  
  return u_materialColor;
}

vec3 getColor(vec3 p, vec3 normal, float distance, float orbitTrap, float steps) {
  vec3 color;

  if (u_colorMode == 0) {
    
    color = getMaterialColor(p);
  } else if (u_colorMode == 1) {
    
    
    float scaled = orbitTrap * u_orbitTrapScale;

    
    float t = fract(scaled);

    
    
    
    

    color = getPaletteColor(t, u_paletteId);
  } else if (u_colorMode == 2) {
    
    float scaled = distance * u_orbitTrapScale * 0.05;
    float t = fract(scaled);
    color = getPaletteColor(t, u_paletteId);
  } else if (u_colorMode == 3) {
    
    color = normal * 0.5 + 0.5;
  } else if (u_colorMode == 4) {
    
    if (!(u_texApplyTarget == 0 || u_texApplyTarget == 2)) {
      color = getMaterialColor(p);
    } else {
      vec3 pt = texSamplePos(p);
      
      bool hasA = (u_worldTexType > 0);
      bool hasB = (u_worldTexTypeB > 0);
      float sA = max(0.05, u_worldTexScale);
      float sB = max(0.05, u_worldTexScaleB);
      float texA = hasA ? worldTextureValue(pt, normal, sA, u_worldTexType) : 0.0;
      float texB = hasB ? worldTextureValue(pt, normal, sB, u_worldTexTypeB) : 0.0;
      float alphaC = clamp(u_worldTexBlendAlphaColor, 0.0, 1.0);
      if (u_texLayerColoring && (hasA || hasB)) {
        vec3 colA = hasA ? mix(u_texA_colorBase, u_texA_colorAccent, clamp(texA, 0.0, 1.0)) : vec3(0.0);
        vec3 colB = hasB ? mix(u_texB_colorBase, u_texB_colorAccent, clamp(texB, 0.0, 1.0)) : vec3(0.0);
        if (u_worldTexBlendMode == 1) {
          color = colA * (hasB ? colB : vec3(1.0));
        } else if (u_worldTexBlendMode == 2) {
          color = clamp(colA + (hasB ? colB : vec3(0.0)), 0.0, 1.0);
        } else {
          color = mix(colA, (hasB ? colB : colA), alphaC);
        }
      } else {
        float t;
        if (u_worldTexBlendMode == 1)      t = texA * (hasB ? texB : 1.0);
        else if (u_worldTexBlendMode == 2) t = clamp(texA + (hasB ? texB : 0.0), 0.0, 1.0);
        else                               t = mix(texA, (hasB ? texB : texA), alphaC);
        color = mix(u_texColorBase, u_texColorAccent, clamp(t, 0.0, 1.0));
      }
    }
  } else {
    
    color = vec3(0.5);
  }

  
  return color * u_colorIntensity;
}

vec3 debugColor(vec3 ro, vec3 rd, vec4 result) {
  
  float steps = result.y;
  float t = result.x;
  bool hit = (result.z > 0.5);
  vec3 p = ro + rd * t;

  if (u_debugMode == 1) {
    
    float s = steps / max(1.0, float(u_maxSteps));
    return vec3(s);
  }
  if (u_debugMode == 2) {
    
    float v = 1.0 - exp(-t * 0.2);
    return vec3(v);
  }
  if (u_debugMode == 3) {
    
    float trap = result.w;
    float n = clamp(trap * 0.5, 0.0, 1.0);
    return getPaletteColor(n, u_paletteId);
  }
  if (u_debugMode == 4) {
    
    if (hit) {
      
      bool isFloorDbg = false;
      if (u_floorEnabled) {
        float dpl = sdPlaneXZ(p + vec3(0.0, 2.0, 0.0));
        isFloorDbg = (abs(dpl) < MIN_DIST * 2.0);
      }
      vec3 normal;
      if (isFloorDbg) normal = vec3(0.0, 1.0, 0.0);
      else normal = (u_fastNormals ? calcNormalFast(p, u_normalEpsilon) : calcNormalEnhanced(p, u_normalEpsilon));
      return normal * 0.5 + 0.5;
    }
    return vec3(0.0);
  }
  if (u_debugMode == 5) {
    
    float d = hit ? abs(mapRaw(p)) : 0.0;
    float v = smoothstep(0.0, 0.01, d); 
    return vec3(v);
  }

  if (u_debugMode == 6) {
    
    vec3 q = p;
    if (u_fractalType == 3) {
      q = spToLocal(q);
    } else if (u_fractalType == 1) {
      q = mgToLocal(q);
      float b = sdBox(q, vec3(1.1));
      float mask = 1.0 - smoothstep(0.04, 0.0, abs(b));
      return vec3(mask);
    } else if (u_fractalType == 4) {
      q = mbxToLocal(q);
    } else {
      vec3 rotated = rotate3D(q, u_rotation);
      q = (rotated - u_zoomCenter) / u_fractalScale;
    }
    float mask;
    if (u_fractalType == 4) {
      float b = sdBox(q, vec3(2.2));
      mask = 1.0 - smoothstep(0.04, 0.0, abs(b));
    } else {
      float r = length(q);
      mask = smoothstep(2.4, 2.2, r) * smoothstep(2.0, 2.2, r);
    }
    return vec3(mask);
  }

  if (u_debugMode == 7) {
    
    float d = mapFractalOnly(p);
    
    float v = clamp(d * 0.5 + 0.5, 0.0, 1.0);
    return vec3(v);
  }

  if (u_debugMode == 8) {
    
    vec3 q = p;
    if (u_fractalType == 3) {
      q = spToLocal(q);
    } else {
      vec3 rotated = rotate3D(q, u_rotation);
      q = (rotated - u_zoomCenter) / u_fractalScale;
    }
    float d = sdTetrahedron(q, 1.8);
    float v = 1.0 - smoothstep(0.0, 0.06, abs(d));
    return vec3(v);
  }

  if (u_debugMode == 9) {
    
    if (hit) {
      bool isFloorDbg = false;
    if (u_floorEnabled) {
      float dpl = sdPlaneXZ(p + vec3(0.0, 2.0, 0.0));
      isFloorDbg = (abs(dpl) < MIN_DIST * 2.0);
    }
    vec3 nWorld = isFloorDbg ? vec3(0.0, 1.0, 0.0) : calcNormalEnhancedFractal(p, u_normalEpsilon);

    
    vec3 nLocal = nWorld;
    if (u_fractalType == 3) {
      nLocal = spDirWorldToLocal(nLocal);
    } else if (u_fractalType == 4) {
      nLocal = rotate3D(nLocal, u_rotation); 
    } else if (u_fractalType == 2) {
      nLocal = rotate3D(nLocal, u_rotation);
    } else if (u_fractalType == 1) {
      nLocal = rotate3D(nLocal, u_rotation);
    } else {
      nLocal = rotate3D(nLocal, u_rotation);
    }
      
      return clamp(nLocal * 0.5 + 0.5, 0.0, 1.0);
    }
    return vec3(0.0);
  }

  
  return vec3(0.0);
}

vec3 ResolveHit(vec3 ro, vec3 rd, float tF, bool hasF, float tP, bool hasP) {
  if (!hasF && !hasP) return vec3(0.0, 0.0, 0.0);
  if (!hasF && hasP)  return vec3(tP, 1.0, 1.0);
  if (hasF && !hasP)  return vec3(tF, 0.0, 1.0);

  float tStar = min(tF, tP);
  float tau = max(MIN_DIST, 2.0 * getAdaptiveEpsilon(tStar));

  if (tP < tF - tau) return vec3(tP, 1.0, 1.0);
  if (tF < tP - tau) return vec3(tF, 0.0, 1.0);

  
  
  vec3 pP = ro + rd * tP;
  float f = mapFractalOnly(pP);
  float epsN = 1.5 * getAdaptiveEpsilon(tP);
  if (abs(f) <= epsN) {
    return vec3(tF, 0.0, 1.0);
  }
  return vec3(tP, 1.0, 1.0);
}

void main() {
  if (u_skipWhenEmpty) {
    fragColor = vec4(u_backgroundColor, 1.0);
    return;
  }
  
  vec2 uv = (gl_FragCoord.xy * 2.0 - u_resolution) / u_resolution.y;

  
  vec3 ro = u_cameraPos;
  vec3 rd = getRayDirectionRobust(uv, ro, u_cameraTarget, u_fov);

  
  vec4 result;
  if (u_useSegmentTracing) {
    result = rayMarchWithTrap_Segment(ro, rd);
  } else if (u_integratorAuto) {
    
    result = rayMarchWithTrap(ro, rd);
  } else {
    result = rayMarchWithTrap(ro, rd);
  }

  
  vec3 color = u_backgroundColor;

  
  if (u_debugEnabled && u_debugMode != 0) {
    color = debugColor(ro, rd, result);
    fragColor = vec4(color, 1.0);
    return;
  }

  
  float tPlaneCand = 0.0;
  bool hasPlane = false;
  if (u_floorEnabled && rd.y < -1e-6) {
    tPlaneCand = (-2.0 - ro.y) / rd.y;
    hasPlane = (tPlaneCand > 0.0);
  }

  
  float tF = result.x;
  bool hasF = (result.z > 0.5);
  vec3 resolved = ResolveHit(ro, rd, tF, hasF, tPlaneCand, hasPlane);
  bool hasHit = (resolved.z > 0.5);

  if (hasHit) {
    float tHit = resolved.x;
    bool isFloor = (resolved.y > 0.5);
    vec3 p = ro + rd * tHit;
    float steps = result.y;
    float orbitTrap = result.w;

    
    vec3 normal;
    if (isFloor) {
      normal = vec3(0.0, 1.0, 0.0);
    } else if (u_useAnalyticNormals && u_fractalType == 2) {
      
      
      
      
      vec3 fp = mbToLocal(p);
      float e = u_normalEpsilon;
      float f0 = sdMandelbulb(fp, u_iterations, u_fractalPower);
      float dx = sdMandelbulb(fp + vec3(e, 0.0, 0.0), u_iterations, u_fractalPower) - f0;
      float dy = sdMandelbulb(fp + vec3(0.0, e, 0.0), u_iterations, u_fractalPower) - f0;
      float dz = sdMandelbulb(fp + vec3(0.0, 0.0, e), u_iterations, u_fractalPower) - f0;
      vec3 nLocal = normalize(vec3(dx, dy, dz));
      
      normal = mbNormalToWorld(nLocal);
    } else {
      
      normal = (u_fastNormals ? calcNormalFast(p, u_normalEpsilon) : calcNormalEnhancedFractal(p, u_normalEpsilon));
    }

    
    
    float specScaleTex = 1.0;
    vec3 matColor;
    if (isFloor) {
      
      float baseChecker = mod(floor(p.x) + floor(p.z), 2.0);
      matColor = mix(u_floorColorA, u_floorColorB, baseChecker);
    } else {
      matColor = getColor(p, normal, tHit, orbitTrap, steps);
      if (u_texturesEnabled && (u_texApplyTarget == 0 || u_texApplyTarget == 2) && ((u_worldDetailStrength > 0.0) || (u_worldTexType > 0) || (u_worldTexTypeB > 0))) {
        vec3 pt = texSamplePos(p);
        float colorFactor; vec3 bumpDelta; float specOut;
        evalProceduralTextures(pt, normal, colorFactor, bumpDelta, specOut);
        if (u_colorMode != 4) {
          matColor *= colorFactor;
        }
        normal = normalize(normal + bumpDelta);
        specScaleTex = specOut;
      }
    }

    
    if (isFloor && (u_texApplyTarget == 1 || u_texApplyTarget == 2) && u_texturesEnabled &&
        ((u_worldDetailStrength > 0.0) || (u_worldTexType > 0) || (u_worldTexTypeB > 0))) {
      
      float disableDist = u_floorTexDisableDist;
      if (u_floorTexAutoDisable) {
        float mul = max(1.0, u_floorTexAutoMul);
        disableDist = u_lodFar * mul;
      }
      bool skipFloorTex = (disableDist > 0.0 && tHit > disableDist);
      if (!skipFloorTex) {
        
        float fadeNear = u_floorFadeNear;
        float fadeFar  = u_floorFadeFar;
        float floorFade = 1.0;
        if (fadeFar > fadeNear) {
          floorFade = 1.0 - smoothstep(fadeNear, fadeFar, tHit);
        } else if (disableDist > 0.0) {
          floorFade = 1.0 - smoothstep(disableDist * 0.6, disableDist * 0.95, tHit);
        }
      if (u_floorTexMode == 0) {
        
        bool hasA = (u_worldTexType > 0);
        bool hasB = (u_worldTexTypeB > 0);
        float sA = max(0.05, u_worldTexScale);
        float sB = max(0.05, u_worldTexScaleB);
        float tA = hasA ? floorTextureValue2D(p, sA, u_worldTexType) : 0.0;
        float tB = hasB ? floorTextureValue2D(p, sB, u_worldTexTypeB) : 0.0;
        float alphaC = clamp(u_worldTexBlendAlphaColor, 0.0, 1.0);
        float alphaN = clamp(u_worldTexBlendAlphaBump, 0.0, 1.0);
        float alphaS = clamp(u_worldTexBlendAlphaSpec, 0.0, 1.0);

        float mixC, mixS;
        float wAc=0.0, wBc=0.0, wAn=0.0, wBn=0.0, wAs=0.0, wBs=0.0;
        if (u_worldTexBlendMode == 1) {
          mixC = tA * (hasB ? tB : 1.0);
          mixS = mixC;
          wAc = (hasB ? tB : 1.0); wBc = tA; wAn = wAc; wBn = wBc; wAs = wAc; wBs = wBc;
        } else if (u_worldTexBlendMode == 2) {
          mixC = clamp(tA + (hasB ? tB : 0.0), 0.0, 1.0);
          mixS = mixC;
          wAc = 1.0; wBc = 1.0; wAn = 1.0; wBn = 1.0; wAs = 1.0; wBs = 1.0;
        } else {
          mixC = mix(tA, (hasB ? tB : tA), alphaC);
          mixS = mix(tA, (hasB ? tB : tA), alphaS);
          wAc = 1.0 - (hasB ? alphaC : 0.0);
          wBc = (hasB ? alphaC : 0.0);
          wAn = 1.0 - (hasB ? alphaN : 0.0);
          wBn = (hasB ? alphaN : 0.0);
          wAs = 1.0 - (hasB ? alphaS : 0.0);
          wBs = (hasB ? alphaS : 0.0);
        }

        
        if (u_colorMode == 4) {
          if (u_texLayerColoring && (hasA || hasB)) {
            vec3 colA = hasA ? mix(u_texA_colorBase, u_texA_colorAccent, clamp(tA, 0.0, 1.0)) : vec3(0.0);
            vec3 colB = hasB ? mix(u_texB_colorBase, u_texB_colorAccent, clamp(tB, 0.0, 1.0)) : vec3(0.0);
            if (u_worldTexBlendMode == 1)      matColor = colA * (hasB ? colB : vec3(1.0));
            else if (u_worldTexBlendMode == 2) matColor = clamp(colA + (hasB ? colB : vec3(0.0)), 0.0, 1.0);
            else                                matColor = mix(colA, (hasB ? colB : colA), alphaC);
          } else {
            matColor = mix(u_texColorBase, u_texColorAccent, clamp(mixC, 0.0, 1.0));
          }
        } else {
          float colA = clamp(u_worldTexColorStrength, 0.0, 1.0);
          float colB = clamp(u_worldTexColorStrengthB, 0.0, 1.0);
          float colW = (u_worldTexBlendMode == 0) ? mix(colA, colB, (hasB ? alphaC : 0.0)) : clamp(colA + (hasB ? colB : 0.0), 0.0, 1.0);
          matColor *= (1.0 + (mixC - 0.5) * 1.0 * colW);
        }

        
        vec3 bumpDelta = vec3(0.0);
        float bA = clamp(u_worldTexBumpStrength, 0.0, 1.0);
        float bB = clamp(u_worldTexBumpStrengthB, 0.0, 1.0);
        float bump = (u_worldTexBlendMode == 0) ? mix(bA, bB, (hasB ? alphaN : 0.0)) : clamp(bA + (hasB ? bB : 0.0), 0.0, 1.0);
        if (bump > 0.0) {
          float epsB = 0.01;
          float t0 = mixC;
          
          float tAx = hasA ? floorTextureValue2D(p + vec3(epsB,0,0), sA, u_worldTexType) : 0.0;
          float tBx = hasB ? floorTextureValue2D(p + vec3(epsB,0,0), sB, u_worldTexTypeB) : 0.0;
          float tAz = hasA ? floorTextureValue2D(p + vec3(0,0,epsB), sA, u_worldTexType) : 0.0;
          float tBz = hasB ? floorTextureValue2D(p + vec3(0,0,epsB), sB, u_worldTexTypeB) : 0.0;
          float mixCx, mixCz;
          if (u_worldTexBlendMode == 1) {
            mixCx = (hasA? tAx:0.0) * (hasB? tBx:1.0);
            mixCz = (hasA? tAz:0.0) * (hasB? tBz:1.0);
          } else if (u_worldTexBlendMode == 2) {
            mixCx = clamp((hasA? tAx:0.0) + (hasB? tBx:0.0), 0.0, 1.0);
            mixCz = clamp((hasA? tAz:0.0) + (hasB? tBz:0.0), 0.0, 1.0);
          } else {
            mixCx = mix((hasA? tAx:t0), (hasB? tBx:t0), alphaN);
            mixCz = mix((hasA? tAz:t0), (hasB? tBz:t0), alphaN);
          }
          vec2 g2 = (vec2(mixCx, mixCz) - vec2(t0)) / epsB;
        bumpDelta = vec3(g2.x, 0.0, g2.y) * 0.4 * bump;
        }
        
        bumpDelta *= clamp(u_floorBumpScale, 0.0, 1.0) * clamp(floorFade, 0.0, 1.0);
        normal = normalize(normal + bumpDelta);

        
        float sPA = clamp(u_worldTexSpecStrength, 0.0, 1.0);
        float sPB = clamp(u_worldTexSpecStrengthB, 0.0, 1.0);
        float sp = (u_worldTexBlendMode == 0) ? mix(sPA, sPB, (hasB ? alphaS : 0.0)) : clamp(sPA + (hasB ? sPB : 0.0), 0.0, 1.0);
        specScaleTex = 1.0 + (mixS - 0.5) * 0.6 * sp;
        
        specScaleTex = 1.0 + (specScaleTex - 1.0) * clamp(u_floorSpecScale, 0.0, 1.0) * clamp(floorFade, 0.0, 1.0);
      } else {
        
        vec3 pt = p;
        float colorFactorF; vec3 bumpDeltaF; float specOutF;
        if (u_floorIgnoreWarp) {
          
          
          colorFactorF = 1.0; bumpDeltaF = vec3(0.0); specOutF = 1.0;
          if (u_worldDetailStrength > 0.0) {
            float dcol = triplanarFbmDetail(pt, normal, max(0.05, u_worldDetailScale));
            float scol = clamp(u_worldDetailStrength, 0.0, 1.0);
            colorFactorF *= (1.0 + (dcol - 0.5) * 0.6 * scol);
          }
          bool hasA = (u_worldTexType > 0);
          bool hasB = (u_worldTexTypeB > 0);
          float sA = max(0.05, u_worldTexScale);
          float sB = max(0.05, u_worldTexScaleB);
          float texA = hasA ? worldTextureValueNoWarp(pt, normal, sA, u_worldTexType) : 0.0;
          float texB = hasB ? worldTextureValueNoWarp(pt, normal, sB, u_worldTexTypeB) : 0.0;
          if (hasA || hasB) {
            float alphaC = clamp(u_worldTexBlendAlphaColor, 0.0, 1.0);
            float alphaN = clamp(u_worldTexBlendAlphaBump, 0.0, 1.0);
            float alphaS = clamp(u_worldTexBlendAlphaSpec, 0.0, 1.0);
            float texMixC, texMixS;
            float wAc=0.0, wBc=0.0, wAn=0.0, wBn=0.0, wAs=0.0, wBs=0.0;
            if (u_worldTexBlendMode == 1) {
              texMixC = texA * (hasB ? texB : 1.0);
              texMixS = texMixC;
              wAc = (hasB ? texB : 1.0); wBc = texA;
              wAn = wAc; wBn = wBc;
              wAs = wAc; wBs = wBc;
            } else if (u_worldTexBlendMode == 2) {
              texMixC = clamp(texA + (hasB ? texB : 0.0), 0.0, 1.0);
              texMixS = texMixC;
              wAc = 1.0; wBc = 1.0;
              wAn = 1.0; wBn = 1.0;
              wAs = 1.0; wBs = 1.0;
            } else {
              texMixC = mix(texA, (hasB ? texB : texA), alphaC);
              texMixS = mix(texA, (hasB ? texB : texA), alphaS);
              wAc = 1.0 - (hasB ? alphaC : 0.0);
              wBc = (hasB ? alphaC : 0.0);
              wAn = 1.0 - (hasB ? alphaN : 0.0);
              wBn = (hasB ? alphaN : 0.0);
              wAs = 1.0 - (hasB ? alphaS : 0.0);
              wBs = (hasB ? alphaS : 0.0);
            }
            float colA = clamp(u_worldTexColorStrength, 0.0, 1.0);
            float colB = clamp(u_worldTexColorStrengthB, 0.0, 1.0);
            float colW = (u_worldTexBlendMode == 0) ? mix(colA, colB, (hasB ? alphaC : 0.0)) : clamp(colA + (hasB ? colB : 0.0), 0.0, 1.0);
            colorFactorF *= (1.0 + (texMixC - 0.5) * 1.0 * colW);

            
            float epsB = 0.01;
            vec3 gx = vec3(epsB,0,0), gy = vec3(0,epsB,0), gz = vec3(0,0,epsB);
            vec3 gradA = vec3(0.0);
            vec3 gradB = vec3(0.0);
            if (hasA) {
              float t0 = texA;
              gradA = vec3(
                worldTextureValueNoWarp(pt + gx, normal, sA, u_worldTexType) - t0,
                worldTextureValueNoWarp(pt + gy, normal, sA, u_worldTexType) - t0,
                worldTextureValueNoWarp(pt + gz, normal, sA, u_worldTexType) - t0
              ) / epsB;
            }
            if (hasB) {
              float t1 = texB;
              gradB = vec3(
                worldTextureValueNoWarp(pt + gx, normal, sB, u_worldTexTypeB) - t1,
                worldTextureValueNoWarp(pt + gy, normal, sB, u_worldTexTypeB) - t1,
                worldTextureValueNoWarp(pt + gz, normal, sB, u_worldTexTypeB) - t1
              ) / epsB;
            }
            vec3 gradT = gradA * wAn + gradB * wBn;
            gradT -= normal * dot(gradT, normal);

            float bA = clamp(u_worldTexBumpStrength, 0.0, 1.0);
            float bB = clamp(u_worldTexBumpStrengthB, 0.0, 1.0);
            float bump = (u_worldTexBlendMode == 0) ? mix(bA, bB, (hasB ? alphaN : 0.0)) : clamp(bA + (hasB ? bB : 0.0), 0.0, 1.0);
            if (u_worldTexAutoAtten) {
              float bigS = max(u_worldTexScale, u_worldTexScaleB);
              float att = 1.0 - smoothstep(18.0, 36.0, bigS) * clamp(u_worldTexAAStrength, 0.0, 1.0);
              bump *= att;
            }
            if (bump > 0.0) bumpDeltaF = gradT * (0.4 * bump);

            float sPA = clamp(u_worldTexSpecStrength, 0.0, 1.0);
            float sPB = clamp(u_worldTexSpecStrengthB, 0.0, 1.0);
            float sp = (u_worldTexBlendMode == 0) ? mix(sPA, sPB, (hasB ? alphaS : 0.0)) : clamp(sPA + (hasB ? sPB : 0.0), 0.0, 1.0);
            if (u_worldTexAutoAtten) {
              float bigS2 = max(u_worldTexScale, u_worldTexScaleB);
              float att2 = 1.0 - smoothstep(18.0, 36.0, bigS2) * clamp(u_worldTexAAStrength, 0.0, 1.0);
              sp *= att2;
            }
            specOutF = 1.0 + (texMixS - 0.5) * 0.6 * sp;
          }
        } else {
          evalProceduralTextures(pt, normal, colorFactorF, bumpDeltaF, specOutF);
        }
        if (u_colorMode == 4) {
          bool hasA = (u_worldTexType > 0);
          bool hasB = (u_worldTexTypeB > 0);
          float sA = max(0.05, u_worldTexScale);
          float sB = max(0.05, u_worldTexScaleB);
          float texA = hasA ? worldTextureValue(pt, normal, sA, u_worldTexType) : 0.0;
          float texB = hasB ? worldTextureValue(pt, normal, sB, u_worldTexTypeB) : 0.0;
          float alphaC = clamp(u_worldTexBlendAlphaColor, 0.0, 1.0);
          if (u_texLayerColoring && (hasA || hasB)) {
            vec3 colA = hasA ? mix(u_texA_colorBase, u_texA_colorAccent, clamp(texA, 0.0, 1.0)) : vec3(0.0);
            vec3 colB = hasB ? mix(u_texB_colorBase, u_texB_colorAccent, clamp(texB, 0.0, 1.0)) : vec3(0.0);
            if (u_worldTexBlendMode == 1)      matColor = colA * (hasB ? colB : vec3(1.0));
            else if (u_worldTexBlendMode == 2) matColor = clamp(colA + (hasB ? colB : vec3(0.0)), 0.0, 1.0);
            else                                matColor = mix(colA, (hasB ? colB : colA), alphaC);
          } else {
            float tTex;
            if (u_worldTexBlendMode == 1)      tTex = texA * (hasB ? texB : 1.0);
            else if (u_worldTexBlendMode == 2) tTex = clamp(texA + (hasB ? texB : 0.0), 0.0, 1.0);
            else                                tTex = mix(texA, (hasB ? texB : texA), alphaC);
            matColor = mix(u_texColorBase, u_texColorAccent, clamp(tTex, 0.0, 1.0));
          }
        } else {
          matColor *= colorFactorF;
        }
        
        bumpDeltaF *= clamp(u_floorBumpScale, 0.0, 1.0) * clamp(floorFade, 0.0, 1.0);
        normal = normalize(normal + bumpDeltaF);
        specScaleTex = 1.0 + (specOutF - 1.0) * clamp(u_floorSpecScale, 0.0, 1.0) * clamp(floorFade, 0.0, 1.0);
      }
      }
    }

    
    vec3 lightDir = normalize(u_lightPos - p);
    vec3 viewDir = normalize(ro - p);
    vec3 halfDir = normalize(lightDir + viewDir);

    
    float diffuse = max(dot(normal, lightDir), 0.0);

    
    float shadow = 1.0;
    if (u_softShadowsEnabled && (!isFloor || u_floorReceiveShadows)) {
      shadow = calcSoftShadowBudgeted(p + normal * 0.01, lightDir, normal, 0.02, length(u_lightPos - p));
      if (u_fastShadows) shadow = max(shadow, 0.7); 
      
      diffuse *= shadow;
    }

    
    float ao = 1.0;
    if (!isFloor) {
      if (u_aoEnabled) {
        ao = calcAOAdvanced(p, normal);
      } else {
        
        float e0 = 0.03;
        float e1 = 0.09;
        float d0 = mapRaw(p + normal * e0);
        float d1 = mapRaw(p + normal * e1);
        float occ0 = 1.0 - clamp((e0 - d0) * 8.0, 0.0, 1.0);
        float occ1 = 1.0 - clamp((e1 - d1) * 4.0, 0.0, 1.0);
        float occ = min(occ0, occ1);
        ao = mix(1.0, occ, clamp(u_aoFallbackStrength, 0.0, 1.0));
      }
    }

    
    float specular = 0.0;
    if (u_softShadowsEnabled) {
      
      if (diffuse > 0.1 && shadow > 0.1) {
        specular = pow(max(dot(normal, halfDir), 0.0), u_shininess);
        
        specular *= smoothstep(0.1, 0.5, diffuse);
      }
    } else {
      
      if (diffuse > 0.3) {
        specular = pow(max(dot(normal, halfDir), 0.0), u_shininess);
        specular *= smoothstep(0.3, 0.7, diffuse);
      }
    }

    
    float ambient = u_ambientStrength * ao;
    vec3 colorFractal = matColor * ambient;
    
    vec3 diffTint = u_tintDiffuse ? u_lightColor : vec3(1.0);
    colorFractal += matColor * (diffuse * u_diffuseStrength) * diffTint;
    
    
    
    colorFractal += u_lightColor * (specular * specScaleTex * u_specularStrength * ao);
    
    if (u_floorEnabled) {
    float dP = abs(sdPlaneXZ(p + vec3(0.0, 2.0, 0.0)));
      float wP = 1.0 - smoothstep(0.02, 0.08, dP); 
      
      colorFractal = mix(colorFractal, colorFractal + matColor * 0.25, wP * 0.8);
    }

    color = colorFractal;

    
    if (u_fogEnabled) {
      float fogFactor = 0.0;

      if (u_fogType == 0) {
        
        fogFactor = 1.0 - exp(-tHit * u_fogDensity);
      } else if (u_fogType == 1) {
        
        fogFactor = 1.0 - exp(-pow(tHit * u_fogDensity, 2.0));
      } else if (u_fogType == 2) {
        
        fogFactor = clamp((tHit - u_fogNear) / (u_fogFar - u_fogNear), 0.0, 1.0);
      }

      
      if (u_enableDithering && u_ditherFog) {
        float n = u_useBlueNoise ? blueNoiseHash(gl_FragCoord.xy * u_blueNoiseScale) : interleavedGradientNoise(gl_FragCoord.xy);
        float amp = u_ditheringStrength * 0.005; 
        fogFactor = clamp(fogFactor + (n - 0.5) * amp, 0.0, 1.0);
      }

      color = mix(color, u_fogColor, fogFactor);
    }
  } else {
    
  }

  
  
  color *= max(0.0, u_postExposure);

  
  if (u_toneMapper == 1) {
    
    const mat3 ACESInputMat = mat3(
      0.59719, 0.35458, 0.04823,
      0.07600, 0.90834, 0.01566,
      0.02840, 0.13383, 0.83777
    );
    const mat3 ACESOutputMat = mat3(
      1.60475,-0.53108,-0.07367,
     -0.10208, 1.10813,-0.00605,
     -0.00327,-0.07276, 1.07602
    );
    vec3 x = ACESInputMat * color;
    vec3 a = x * (x + 0.0245786) - 0.000090537;
    vec3 b = x * (0.983729 * x + 0.4329510) + 0.238081;
    color = ACESOutputMat * (a / b);
  } else if (u_toneMapper == 2) {
    
    vec3 x = max(vec3(0.0), color - 0.004);
    color = (x * (6.2 * x + 0.5)) / (x * (6.2 * x + 1.7) + 0.06);
  }

  
  float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
  color = mix(vec3(luma), color, clamp(u_postSaturation, 0.0, 2.0));
  
  color = (color - 0.5) * clamp(u_postContrast, 0.0, 4.0) + 0.5;
  
  {
    vec2 uv01 = gl_FragCoord.xy / u_resolution.xy;
    float aspect = u_resolution.x / max(u_resolution.y, 1.0);
    vec2 p = (uv01 - 0.5) * vec2(aspect, 1.0);
    float r = length(p) * 1.2; 
    float soft = clamp(u_vignetteSoftness, 0.0, 1.0);
    float v = smoothstep(max(0.0, 1.0 - soft), 1.0, r);
    color *= (1.0 - clamp(u_vignetteStrength, 0.0, 1.0) * v);
  }
  
  float g = max(0.01, u_postGamma);
  color = pow(max(color, 0.0), vec3(1.0 / g));
  
  color = clamp(color, 0.0, 1.0);

  fragColor = vec4(color, 1.0);
}

float mapFractalOnly(vec3 p) {
  float fractal = MAX_DIST;
  switch (u_fractalType) {
    case FT_PRIMITIVES: {
      vec3 spherePos = vec3(sin(u_time * 0.75) * 2.0, 0.0, 0.0);
      float sphere = sdSphere(p - spherePos, 1.0);
      vec3 boxPos = vec3(0.0, 0.0, 0.0);
      float box = sdBox(rotate3D(p - boxPos, u_rotation), vec3(0.8, 0.8, 0.8));
      fractal = opSubtraction(box, sphere);
    } break;
    case FT_MENGER: {
      vec3 fp = mgToLocal(p);
      fractal = sdMenger(fp, u_iterations) * u_fractalScale;
    } break;
    case FT_BULB: {
      vec3 fp = mbToLocal(p);
      fractal = sdMandelbulb(fp, u_iterations, u_fractalPower) * u_fractalScale;
    } break;
    case FT_SIERPINSKI: {
      vec3 fp = spToLocal(p);
      fractal = sdSierpinskiWithBase(fp, u_iterations, 2.0, max(0.2, u_sierpinskiBase)) * u_fractalScale;
    } break;
    case FT_MANDELBOX: {
      vec3 fp = mbxToLocal(p);
      fractal = sdMandelboxSimple(fp, u_iterations, -1.5) * (u_fractalScale * MB_NORM);
    } break;
    case FT_WORLD: {
      float trapDummy; fractal = deAmazingSurfWorld(p, trapDummy);
    } break;
    case FT_TRUCHET: {
      float trapDummy; fractal = deTruchetPipesWorld(p, trapDummy);
    } break;
    case FT_DEC: {
      float s = max(0.2, u_fractalScale);
      vec3 rotated = rotate3D(p - u_decOffset, u_rotation);
      vec3 pl = (rotated - u_zoomCenter) / s;
      float d = decUserDE(pl) * s;
      fractal = d;
    } break;
  }
  return fractal;
}`